import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Stepper } from '@/components/ui/stepper';
import { isMonday } from './online-date';
import { StatusBadge } from './StatusBadge';
import { MailingPanel } from './MailingPanel';
import { formatDepot } from './depot-format';
import { formatAuthor } from './author-format';
import { DepotAutosuggest } from './DepotAutosuggest';
import { RichTextEditor } from './RichTextEditor';
import { categoriesOf, textsInCategory, isBlankMarkdown, plainTextLength } from './event-text-pick';
import { isValidIban, isValidBic } from './bank-validation';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { EventSeriesDialog } from './EventSeriesDialog';
import { useEventTexts, useSalesChannels, useMailings, useCancellationTerms, usePaymentNoteTemplates, useFinancialSupport } from '@/services/event-service';
import { resolveFinancialSupport } from '@/lib/financial-support-matching';
import { toPriceFromNet } from '@/lib/price';
import { formatCurrency } from '@/lib/i18n/formatters';
import { apiClient } from '@/lib/api-client';
import {
  DepotEvent,
  EventStatus,
  EventOrigin,
  EventAudience,
  EventText,
  CancellationTerm,
  PaymentNoteTemplate,
  LocationType,
  PriceUnit,
  TaxCategoryKey,
  TAX_CATEGORY_OPTIONS,
  DEFAULT_TAX_CATEGORY,
  EVENT_REASON_OPTIONS,
  PaymentMethod,
  DepotRef,
  EventType,
  Role,
  CreateEventRequest,
} from '@/types';
import { Check, Send, Undo2, X, Save, Trash2, ExternalLink, Info, Copy } from 'lucide-react';

export type DialogMode = 'create' | 'edit' | 'review';

interface Props {
  open: boolean;
  mode: DialogMode;
  event: DepotEvent | null; // null in create mode
  /**
   * Source for a copy: create mode, prefilled from an existing event.
   *
   * Deliberately not `event` — that would switch the dialog to edit mode and
   * overwrite the original.
   */
  copySource?: DepotEvent | null;
  role: Role | undefined;
  userDepots: DepotRef[]; // depots the current user may use as origin
  allDepots: DepotRef[]; // all depots (promoting + central origin)
  eventTypes: EventType[];
  onOpenChange: (open: boolean) => void;
  onSaveCreate: (payload: CreateEventRequest) => void;
  /**
   * `keepOpen` (24.09.2026, Bugfix): true only from "Speichern & weiter" on
   * tab 1/2 — the dialog must stay open and advance to the next tab, unlike
   * every other caller (plain "Speichern", the qty-only save), which saves
   * and closes as before. Without this, "Speichern & weiter" on an existing
   * VA saved correctly but the dialog closed anyway, because the parent's
   * onSaveUpdate handler always closed it.
   */
  onSaveUpdate: (payload: Partial<CreateEventRequest>, keepOpen?: boolean) => void;
  /**
   * Every transition below takes an optional payload: whatever the form has
   * pending gets saved together with the status change, not just the status
   * (bug fix 09.09.2026). Before this, clicking e.g. "Freigeben" straight after
   * editing a field — a central reviewer's own form stays fully editable
   * during in_review, see `readOnly`/`frozenForPartner` below — sent no field
   * data at all: the edit was silently lost, only the status moved. The
   * caller (EventListPage) saves the payload first, then performs the
   * transition, so this stays a single user-visible action.
   */
  onSubmit: (payload?: Partial<CreateEventRequest>) => void;
  /**
   * "Zur Prüfung" in create mode — there is no existing event to submit yet.
   * Creates it and submits the newly created one in the same action (bug fix
   * 08.09.2026: handleSubmitForReview used to call onSubmit() unconditionally,
   * which assumes activeEvent already exists — in create mode it does not, so
   * the click silently did nothing).
   */
  onSubmitCreate: (payload: CreateEventRequest) => void;
  onApprove: (payload?: Partial<CreateEventRequest>) => void;
  /** Rejecting neither validates nor saves the form (Nutzervorgabe, 25.09.2026) — only status+reason. */
  onReject: (reason: string) => void;
  onReopen: (payload?: Partial<CreateEventRequest>) => void;
  /** Absent when this role/status/date combination may not cancel right now. */
  onCancelEvent?: (payload?: Partial<CreateEventRequest>) => void;
  /** Absent until the event has ended — see eventHasEnded (backend access-control.ts). */
  onCompleteEvent?: (payload?: Partial<CreateEventRequest>) => void;
  /** Absent unless status is Completed — opens the Abrechnung wizard (DCP-2228). */
  onOpenRefund?: () => void;
  /** Absent when the user may not delete this event. */
  onDelete?: () => void;
}

type FormState = CreateEventRequest;

// ---- datetime helpers: split ISO <-> date + time inputs ----------------------
export function splitDateTime(iso?: string): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  const pad = (n: number) => String(n).padStart(2, '0');
  // Von combineDateTime() ohne echte Uhrzeit geschrieben (Geschäftszeiten-VA) —
  // dort bewusst als UTC-Mitternacht kodiert, nicht über die lokale Zeitzone
  // (siehe dort). Symmetrisch beim Lesen: Datum über UTC lesen, keine Uhrzeit
  // zurückgeben — sonst zeigt das Formular ein anderes Datum als gespeichert
  // ist (Bug gefunden 17.09.2026).
  if (iso.endsWith('T00:00:00.000Z')) {
    return {
      date: `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`,
      time: '',
    };
  }
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}
export function combineDateTime(date: string, time: string): string {
  if (!date) return '';
  if (!time) {
    // Keine reale Uhrzeit (Geschäftszeiten-VA) — Mitternacht ist hier nur ein
    // Platzhalter, keine tatsächliche Startzeit. Direkt als UTC-Mitternacht
    // kodieren statt über new Date()s implizite lokale Zeitzone: sonst rutscht
    // "00:00 Berlin" 1-2 Stunden in den UTC-Vortag, und das VA-Datum zeigt sich
    // in commercetools/downstream einen Tag zu früh (Bug gefunden 17.09.2026:
    // 19.09. gespeichert als 2026-09-18T22:00:00Z statt 2026-09-19T00:00:00Z).
    return `${date}T00:00:00.000Z`;
  }
  const d = new Date(`${date}T${time}`);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

/**
 * Freie Uhrzeit-Eingabe mit eigener Validierung (24.09.2026, Nutzervorgabe) —
 * zurück vom 00/30-Select-Zwischenschritt vom 21.09.2026: die feste
 * Minuten-Rasterung wirkte einschränkend. Format strikt `HH:mm`, 00–23/00–59
 * (kein 12-Stunden-Format, keine einstelligen Stunden) — geprüft in
 * validate() gegen startTimeText/endTimeText, nicht gegen das abgeleitete
 * form.startAt/endAt (siehe die beiden States oben, wo die Uhrzeit während
 * der Eingabe bewusst unabhängig vom Datumsfeld gehalten wird).
 */
export const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Setzt den Trenner ":" automatisch nach den ersten zwei Ziffern
 * (24.09.2026, Nutzervorgabe) — z. B. "1830" wird beim Tippen zu "18:30",
 * ohne dass der Doppelpunkt selbst eingegeben werden muss. Immer frisch aus
 * den reinen Ziffern des aktuellen Feldwerts abgeleitet (nicht inkrementell
 * verändert), damit Löschen (Backspace) genauso funktioniert wie Tippen —
 * bei nur noch zwei Ziffern verschwindet der Doppelpunkt wieder von selbst.
 * Über 4 Ziffern hinaus wird nichts mehr angenommen (kein 5-stelliges "HH:mm").
 */
export function formatTimeInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  return digits.length <= 2 ? digits : `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

/** Heute als yyyy-mm-dd, lokale Zeitzone — Default für "Geplante Onlinestellung" bei Neuanlage. */
export function todayDateOnly(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Ob "Zur Prüfung" angeboten wird: beim Anlegen (noch kein Status) sowie aus
 * "in Erfassung" und "nicht freigegeben". Aus allen anderen Status gibt es einen eigenen
 * Knopf (Freigeben, Wieder öffnen) oder keinen — "Zur Prüfung" wäre dort entweder falsch
 * oder vom Server abgelehnt (PARTNER_ALLOWED_TRANSITIONS im Backend).
 */
export function canSubmitForReview(status: EventStatus | undefined): boolean {
  return status === undefined || status === EventStatus.Initial || status === EventStatus.Rejected;
}

/**
 * Nächster Montag, oder heute, wenn heute Montag vor 12 Uhr mittags ist
 * (Nutzervorgabe, 18.09.2026) — neuer Default für "Geplante Onlinestellung"
 * bei Neuanlage, ersetzt den bisherigen reinen todayDateOnly()-Default.
 */
export function nextMondayOrToday(): string {
  const now = new Date();
  const day = now.getDay(); // 0=So..6=Sa, 1=Mo
  if (day === 1 && now.getHours() < 12) return todayDateOnly();
  // (8-day)%7 liefert für Montag 0 statt der gewünschten 7 (nächste Woche,
  // da der obige Zweig "Montag vor 12 Uhr" schon abgefangen hat) — "|| 7"
  // fängt genau diesen Fall.
  const daysUntilMonday = ((8 - day) % 7) || 7;
  const target = new Date(now);
  target.setDate(now.getDate() + daysUntilMonday);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}`;
}

/**
 * Datumsteil aus einem gespeicherten Wert für ein reines Datumsfeld
 * (plannedOnlineDate/registrationDeadline/paymentDeadline) — funktioniert für
 * beide Formate gleich, die dort vorkommen können (DCP-2228 follow-up,
 * 18.09.2026): admin-app schrieb diese drei Felder bisher als reines
 * `YYYY-MM-DD`, ab jetzt wie die Altdaten aus dem six-cms-Import als volles
 * ISO-Datetime auf UTC-Mitternacht (siehe combineDateTime) — ein
 * `<input type="date">` braucht in beiden Fällen nur die ersten 10 Zeichen,
 * kein new Date()/keine Zeitzonenumrechnung nötig.
 */
export function dateOnlyPart(value?: string): string {
  return value?.slice(0, 10) ?? '';
}

/**
 * `days` Kalendertage vor `dateOnly` (yyyy-mm-dd), als UTC-Mitternacht-
 * Arithmetik gerechnet (25.09.2026) — vermeidet DST-Kanten, die eine lokale
 * `Date`-Subtraktion in die Irre führen könnten. Ausgelagert aus den beiden
 * fast identischen Inline-Berechnungen bei "mit Anmeldung"/"Kostenpflichtig".
 */
function daysBefore(dateOnly: string, days: number): string {
  const d = new Date(`${dateOnly}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() - days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/**
 * Automatischer Vorschlag für Anmelde-/Zahlungsfrist: 3 Tage vor Beginn,
 * aber nie vor der geplanten Onlinestellung (25.09.2026, Nutzervorgabe) —
 * beide Fristen dürfen laut validate() nicht davor liegen
 * (`deadlineBeforeOnlineDate`), sonst hätte der Vorschlag selbst sofort
 * einen Fehler ausgelöst (z. B. bei einer sehr kurzfristig angelegten VA).
 */
function deadlineDefault(startDate: string, plannedOnlineDate?: string): string {
  const before = daysBefore(startDate, 3);
  const onlineDay = dateOnlyPart(plannedOnlineDate);
  return onlineDay && before < onlineDay ? onlineDay : before;
}

/**
 * Deutsche Umlaut-Transliteration für den Slug (DCP-2228 follow-up,
 * 18.09.2026-Follow-up) — "Käse" -> "kaese", nicht das generische
 * Unicode-Diakritika-Stripping ("kase"), das für deutsche Wörter falsch
 * wäre. Spiegelt backend/src/events/slug.ts.
 */
const GERMAN_UMLAUTS: Record<string, string> = {
  ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss',
  Ä: 'Ae', Ö: 'Oe', Ü: 'Ue',
};

/**
 * Slugifiziert den Titel-Anteil (DCP-2228 follow-up, 18.09.2026-Follow-up,
 * Nutzerwunsch): Umlaute transliterieren, klein schreiben, alles außer
 * `[a-z0-9_-]` (Satzzeichen, Leerzeichen, sonstige Sonderzeichen) zu einem
 * einzelnen Bindestrich zusammenfassen, führende/folgende Bindestriche
 * abschneiden. Ersetzt statt zu verbieten — das Muster-Feld
 * (`[a-zA-Z0-9_-]{2,256}`, validate() weiter unten) bleibt trotzdem als
 * Sicherheitsnetz für einen händisch eingetippten Admin-Wert.
 */
function slugifyTitle(title: string): string {
  const transliterated = title.replace(/[äöüÄÖÜß]/g, (c) => GERMAN_UMLAUTS[c] ?? c);
  return transliterated
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    // Collapses runs of hyphens that survive the replace above unmerged — a
    // literal "--" in the title is already inside [a-z0-9_-], so the regex
    // above never touches it, and a space right next to it would otherwise
    // add yet another hyphen alongside it (bug found 18.09.2026-Follow-up).
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * TT-MM-JJJJ für den Slug-Vorschlag — dieselbe UTC-Sonderbehandlung wie
 * splitDateTime() oben. `undefined` bei einem nicht parsebaren Wert (Bug
 * gefunden 18.09.2026: eine leere/unvollständige Zwischen-Eingabe im
 * Datums-Feld erzeugte zuvor "NaN-NaN-NaN" im Slug, weil hier ungeprüft
 * weitergerechnet wurde) — buildSlugSuggestion() gibt dann selbst nichts
 * zurück, statt einen kaputten Wert zu speichern.
 */
function formatDatePartForSlug(iso: string): string | undefined {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (iso.endsWith('T00:00:00.000Z')) {
    return `${pad(d.getUTCDate())}-${pad(d.getUTCMonth() + 1)}-${d.getUTCFullYear()}`;
  }
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

/**
 * "Sprechende URL"-Vorschlag aus Titel + Datum (DCP-2228 follow-up,
 * 18.09.2026) — siehe slugifyTitle() für den Titel-Anteil. Spiegelt
 * backend/src/events/slug.ts — dieselbe Logik für den VA-Massenimport, der
 * kein Formular zum Vorschauen hat; kann wegen der getrennten
 * Frontend-/Backend-Builds nicht direkt geteilt werden.
 *
 * `undefined`, wenn Titel oder eines der beiden Daten (noch) nicht
 * verwertbar ist — der Aufrufer lässt das Feld dann einfach leer, statt
 * "NaN-NaN-NaN" hineinzuschreiben.
 */
function buildSlugSuggestion(title: string, startAt: string, endAt: string): string | undefined {
  const titlePart = slugifyTitle(title);
  const startDate = formatDatePartForSlug(startAt);
  const endDate = formatDatePartForSlug(endAt);
  if (!titlePart || !startDate || !endDate) return undefined;

  return startDate === endDate
    ? `${titlePart}-am-${startDate}`
    : `${titlePart}-vom-${startDate}-bis-${endDate}`;
}

/**
 * Mergt neu ausgewählte Depots additiv in eine bestehende Liste, ohne
 * Duplikate (15.09.2026) — für "bewerbende Depots automatisch vorausgewählt".
 * Rein additiv: ein manuell wieder aus `existing` entferntes Depot wird durch
 * einen späteren Aufruf nicht erneut hineingemischt.
 */
function mergeDepotsByKey(existing: DepotRef[], additions: DepotRef[]): DepotRef[] {
  const seen = new Set(existing.map((d) => d.key));
  const toAdd = additions.filter((d) => !seen.has(d.key));
  return toAdd.length > 0 ? [...existing, ...toAdd] : existing;
}

function emptyForm(role: Role | undefined, userDepots: DepotRef[]): FormState {
  const isPartner = role === Role.Partner || role === Role.Mitarbeiter;
  // Beim Erstellen automatisch auch als bewerbendes Depot vorausgewählt
  // (15.09.2026) — nur der Partner-Default hat hier schon ein Depot; die
  // Zentrale wählt ihres erst interaktiv (siehe das Einzel-Select's onChange).
  const defaultDepot = isPartner && userDepots.length > 0 ? [userDepots[0]] : [];
  return {
    origin: isPartner ? EventOrigin.Partner : EventOrigin.Central,
    depots: defaultDepot,
    promotingDepots: defaultDepot,
    vaType: '',
    title: '',
    teaser: '',
    description: '',
    presetTextId: undefined,
    audience: EventAudience.Public,
    startAt: '',
    endAt: '',
    businessHours: false,
    plannedOnlineDate: combineDateTime(nextMondayOrToday(), ''),
    costEnabled: true,
    priceUnit: 'person',
    taxCategory: DEFAULT_TAX_CATEGORY,
    registrationRequired: false,
    // Default "Im Depot oder per Überweisung" (24.09.2026, Nutzervorgabe) —
    // die zugehörige Vorlage wird erst nachträglich per Effekt eingesetzt
    // (siehe dort), weil paymentNoteTemplates hier noch nicht geladen ist.
    paymentMethod: 'depot_or_transfer',
    // Default "sichtbar" (15.09.2026) — bewahrt das bisherige Verhalten (Kontodaten
    // immer sichtbar) für jeden, der den neuen Switch nicht anfasst.
    publishAccountData: true,
    locationType: 'depot',
    financialSupportApproved: false,
  };
}

/**
 * `available`/`sold`/`ticketed` are read-only, backend-derived fields (see
 * CreateEventRequest's Omit) — the backend rejects them with 400 if a save
 * echoes them back. DepotEvent carries them, so they must be stripped here,
 * not just at the copy prefill (US-103 follow-up, found 06.08.2026).
 */
function toForm(e: DepotEvent): FormState {
  // Bug fix 08.09.2026: createdByRole was missing here, even though
  // CreateEventRequest (types/index.ts) already excludes it — the backend's
  // rejectServerManagedFields() 400s on it ("read-only — authorship is taken
  // from the signed-in user"). Every event carries createdByRole since the
  // role-switcher feature (CtEventRepository.create() sets it unconditionally,
  // not just for role-switched creators), so editing ANY existing event and
  // saving sent it straight back and failed — not specific to rejected VAs,
  // that just happened to be the flow that surfaced it.
  //
  // availableMailings was missing too, same root cause — CreateEventRequest
  // excludes it as well. Silent rather than a 400 (the backend does not reject
  // it, and CtEventRepository.update() applies it like any other input field,
  // unlike createdBy/createdByRole/createdByPartner which it forces back to
  // the stored value): round-tripping the unchanged value back is harmless in
  // the ordinary load→edit→save flow, but the field is meant to be an
  // immutable audit snapshot taken once at creation, not something a client
  // should be able to send at all.
  const {
    identifier, createdAt, updatedAt, createdBy, createdByRole, modifiedBy, status, available, sold, ticketed,
    availableMailings, eventsVersion,
    ...rest
  } = e;
  void identifier; void createdAt; void updatedAt; void createdBy; void createdByRole; void modifiedBy;
  void status; void available; void sold; void ticketed; void availableMailings; void eventsVersion;
  return rest;
}

/**
 * Prefill for a copy: everything except the dates.
 *
 * startAt/endAt are cleared deliberately rather than carried over — a copy is
 * almost always the same event on a different day, and keeping the old date would
 * make it far too easy to save a duplicate on the original date. Both are
 * required fields, so validation forces the user to pick new ones.
 *
 * `qty` IS carried over — it is what the copy should sell.
 *
 * `slug` is dropped too (DCP-2228 follow-up, 18.09.2026): it was built from
 * the original's title+date, so carrying it over would tie the copy's URL to
 * data that no longer applies — a fresh one is generated once a new date is
 * entered (see the effect near `set()`).
 */
function toCopyForm(e: DepotEvent): FormState {
  return { ...toForm(e), startAt: '', endAt: '', slug: undefined };
}

/**
 * Belegte Plätze, die beim Senken der Anzahl nicht angetastet werden dürfen —
 * reserviert UND verkauft, nicht nur verkauft (12.10.2026, Nutzervorgabe,
 * löst die bisherige Nur-`sold`-Prüfung ab): ein reserviertes, aber noch
 * nicht abgeschlossenes Ticket (z. B. im Checkout) mindert das Kontingent
 * genauso lange, bis es storniert wird, und müsste sonst beim Herunterdrehen
 * der Anzahl mit verdrängt werden können — fachlich falsch, solange es noch
 * besteht. `qty`/`available` kommen beide von der ticket-api (siehe
 * available.ts's withAvailable()); ohne eine der beiden ist nichts
 * verlässlich berechenbar, dann lieber 0 (kein Client-seitiger Floor) als
 * ein falscher Wert — der Server bleibt ohnehin die eigentliche Instanz.
 */
function committedSeats(event: DepotEvent | null): number {
  if (!event || event.qty == null || event.available == null) return 0;
  return event.qty - event.available;
}

/**
 * Welcher Tab ein validate()-Fehlerfeld enthält — für den Sprung zum ersten
 * gefundenen Fehler. Felder ohne Eintrag hier lösen keinen Tab-Wechsel aus
 * (aktuell prüft validate() keine Felder aus Tab 2).
 */
const ERROR_FIELD_TAB: Record<string, 1 | 2 | 3> = {
  title: 1,
  slug: 1,
  vaType: 1,
  depots: 1,
  promotingDepots: 1,
  audience: 1,
  startAt: 1,
  endAt: 1,
  time: 1,
  plannedOnlineDate: 1,
  externalName: 1,
  externalStreet: 1,
  externalHouseNo: 1,
  externalZip: 1,
  externalCity: 1,
  externalCountry: 1,
  description: 2,
  priceAmount: 3,
  qty: 3,
  salesChannels: 3,
  paymentIban: 3,
  paymentBic: 3,
  glassOrTableSale: 3,
  paymentNote: 3,
  paymentAccountHolder: 3,
  paymentReference: 3,
  registrationDeadline: 3,
  paymentDeadline: 3,
};

/**
 * Teaserzeile temporär ausgeblendet (Nutzervorgabe, 18.09.2026) — kommt
 * später wieder, deshalb bewusst nicht gelöscht, nur stillgelegt.
 */
const TEASER_TEMPORARILY_HIDDEN = true;

/** Storefront base URL for the "Vorschau" link (24.09.2026) — see vite-env.d.ts. */
const STOREFRONT_BASE_URL = import.meta.env.VITE_STOREFRONT_BASE_URL ?? '';

export function EventFormDialog({
  open,
  mode,
  event,
  copySource,
  role,
  userDepots,
  allDepots,
  eventTypes,
  onOpenChange,
  onSaveCreate,
  onSaveUpdate,
  onSubmit,
  onSubmitCreate,
  onApprove,
  onReject,
  onReopen,
  onCancelEvent,
  onCompleteEvent,
  onOpenRefund,
  onDelete,
}: Props) {
  const { t } = useTranslation('events');
  // Eigener Namensraum für die Rollennamen: sie liegen in users.json (Rollenwechsler)
  // und sollen nicht ein zweites Mal in events.json stehen.
  const { t: tUsers } = useTranslation('users');
  // Die pflegbaren Textvorlagen. `usePresetTexts` (drei Beispiele aus dem Code) wird
  // hier nicht mehr gelesen — die Route bleibt bestehen, hat aber keinen Verbraucher
  // mehr im Dialog.
  const { data: eventTexts = [] } = useEventTexts();
  const { data: salesChannels = [] } = useSalesChannels();
  const { data: mailings = [], isLoading: mailingsLoading } = useMailings();
  // Live-Vorschau der finanziellen Unterstützung (14.09.2026) — nur für
  // Admin/Zentrale sichtbar, siehe isCentral weiter unten. Trotzdem hier
  // unconditional geladen (nicht erst bei isCentral): der Hook selbst ist
  // günstig (5 Min. Cache, wird ohnehin für die Übersichtsseite gebraucht)
  // und ein bedingter Hook-Aufruf verstößt gegen die React-Regeln.
  const { data: financialSupportEntries = [] } = useFinancialSupport();
  // Ein einziges Auswahlfeld mit allen sichtbaren Bedingungen (US-304) — keine
  // Kategorie-Kaskade wie beim Beschreibungstext (Entscheidung 3.1).
  const { data: cancellationTerms = [] } = useCancellationTerms();
  // Vorlagenliste für Zahlung (Rich-Text-Umstellung, Teil 3) — flach, wie die
  // Stornobedingungen. Der Server generiert seit diesem Umbau nichts mehr
  // automatisch; die Auswahl kopiert nur.
  const { data: paymentNoteTemplates = [] } = usePaymentNoteTemplates();
  const [form, setForm] = useState<FormState>(() => emptyForm(role, userDepots));
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Tab-Sprung-Auslöser (24.09.2026, Bugfix) — der Auto-Sprung zum ersten
  // Fehlerfeld darf NICHT an jede errors-Änderung hängen: validate() setzt
  // immer das volle formularweite Fehler-Set, auch wenn handleSaveAndContinue
  // es nur aufruft, um die Fehler des AKTUELLEN Tabs zu lesen (siehe dort) —
  // ein leeres Tab 3 (z. B. fehlender Preis) sprang dadurch beim Weiterblättern
  // von Tab 1 zu Tab 3, statt auf Tab 2 zu landen. Nur "finale" Aktionen
  // (Speichern/Einreichen/Statuswechsel), die bei einem Fehler wirklich
  // abbrechen, zählen diesen Zähler hoch; handleSaveAndContinue tut das
  // bewusst nicht.
  const [errorJumpTrigger, setErrorJumpTrigger] = useState(0);
  // Freie Uhrzeit-Eingabe (24.09.2026, zurück vom 00/30-Select) — eigener,
  // von form.startAt/endAt UNABHÄNGIGER Text-State während der Eingabe: ein
  // an form.startAt gebundenes Feld würde bei jedem Tastendruck, der noch
  // keine gültige Uhrzeit ergibt, über combineDateTime() auf '' zurückfallen
  // und das gerade Getippte sofort wieder löschen (derselbe Bug, der am
  // 21.09.2026 zum Select-Umbau führte). form.startAt/endAt wird erst
  // geschrieben, sobald der Text vollständig gültig ist (siehe die beiden
  // onChange-Handler unten); validate() prüft den rohen Text, nicht das
  // abgeleitete Datumsfeld.
  const [startTimeText, setStartTimeText] = useState('');
  const [endTimeText, setEndTimeText] = useState('');
  // Sprechende URL (DCP-2228 follow-up, 18.09.2026): solange dieses Flag
  // false ist UND der Server noch keinen Slug für diese VA kennt, folgt
  // form.slug weiterhin live Titel+Datum — auch nach dem ersten Vorschlag,
  // damit ein Tippfehler im Titel oder ein falsch gewähltes Datum den Slug
  // vor dem Speichern noch korrigiert. `true`, sobald Admin das Feld selbst
  // anfasst (dann übernimmt der Mensch die Kontrolle, kein Autoschreiben mehr).
  const [slugTouched, setSlugTouched] = useState(false);
  // Bestes-Wissen-Hinweis, ob der aktuelle Slug an diesem Depot schon vergeben
  // ist (DCP-2228 follow-up, 18.09.2026, Nutzerwunsch) — rein informativ, kein
  // Ersatz für die serverseitige Prüfung beim Speichern (CtEventRepository.ts).
  const [slugConflict, setSlugConflict] = useState(false);
  const [confirmReject, setConfirmReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  // Gegen Fehlklicks (22.09.2026-Follow-up): dieselbe zweistufige Bestätigung
  // wie beim Ablehnen, nur ohne Freitext — ein reiner Ja/Nein-Zwischenschritt.
  const [confirmCancelEvent, setConfirmCancelEvent] = useState(false);
  const [confirmCompleteEvent, setConfirmCompleteEvent] = useState(false);
  const [confirmReopenEvent, setConfirmReopenEvent] = useState(false);
  // Tabs (15.09.2026) — freies Hin- und Herspringen, kein Sequenzzwang wie beim
  // Refund-Wizard; alle drei Tabs teilen sich denselben form-State.
  const [activeTab, setActiveTab] = useState<1 | 2 | 3>(1);
  // Scrollbarer Content-Wrapper (siehe unten, "flex-1 overflow-y-auto") — für
  // den Scroll-nach-oben bei manuellem Tab-Wechsel (Nutzervorgabe, 19.09.2026).
  const contentRef = useRef<HTMLDivElement>(null);
  /** Wie setActiveTab(n), zusätzlich Scroll-Reset — nur für manuelle
   *  Tab-Wechsel (Klick, "Speichern & weiter"), NICHT für die programmatischen
   *  Sprünge (Dialog-Open-Reset, Fehler-Sprung): dort ist kein "Wechsel",
   *  sondern eine initiale Zuweisung bzw. ein gezielter Fehler-Sprung, bei dem
   *  man ggf. gerade NICHT nach oben scrollen will. */
  const goToTab = (n: 1 | 2 | 3) => {
    setActiveTab(n);
    contentRef.current?.scrollTo({ top: 0 });
  };

  // Kategorie der Vorlagen-Auswahl. Nur Anzeigezustand, wird nicht gespeichert: am
  // Event steht die presetTextId, und aus der ließe sich die Kategorie jederzeit
  // wieder ableiten.
  const [presetCategory, setPresetCategory] = useState('');
  // Vorlage, deren Einsetzen noch bestätigt werden muss (nur bei gefülltem Text).
  const [pendingPreset, setPendingPreset] = useState<EventText | null>(null);
  // Stornobedingung, deren Einsetzen noch bestätigt werden muss (nur bei gefülltem Text) —
  // dasselbe Muster wie pendingPreset, seit der Wortlaut hier ebenfalls frei editierbar ist.
  const [pendingCancellationTerm, setPendingCancellationTerm] = useState<CancellationTerm | null>(
    null
  );
  // Methode, deren Vorschlagstext noch bestätigt werden muss (nur bei gefülltem
  // Text) — dasselbe Muster, jetzt für paymentNote.
  const [pendingPaymentNoteTemplate, setPendingPaymentNoteTemplate] =
    useState<PaymentNoteTemplate | null>(null);

  const eventTextCategories = useMemo(() => categoriesOf(eventTexts), [eventTexts]);
  const presetOptions = useMemo(
    () => textsInCategory(eventTexts, presetCategory),
    [eventTexts, presetCategory]
  );

  const isCentral = role === Role.Zentral || role === Role.Admin;
  const isPartner = role === Role.Partner || role === Role.Mitarbeiter;
  // Sprechende URL (DCP-2228 follow-up, 18.09.2026): nur Admin darf sie
  // editieren, enger als isCentral (das schließt Zentrale mit ein).
  const isAdmin = role === Role.Admin;

  // Live-Zuordnung aus der Pflege "Finanzielle Unterstützung" — reagiert sofort
  // auf VA-Typ/Teilnehmerkreis, ohne den Server zu fragen (siehe Hook oben).
  const matchedFinancialSupport = useMemo(
    () => resolveFinancialSupport(financialSupportEntries, form.vaType, form.audience),
    [financialSupportEntries, form.vaType, form.audience]
  );

  // Client-side mirror of the server's date gates (access-control.ts:
  // partnerCancelWindowAllows/eventHasEnded) plus the status preconditions
  // (ALLOWED_STATUS_TRANSITIONS/PARTNER_ALLOWED_TRANSITIONS) — UX only, so a
  // doomed request never leaves the browser. The server stays the actual
  // authority; a stale clock here just means the button shows and the server
  // then refuses it.
  const today = new Date().toISOString().slice(0, 10);
  const CANCELLABLE_STATUSES: readonly EventStatus[] = [
    EventStatus.Initial,
    EventStatus.Rejected,
    EventStatus.InReview,
    EventStatus.Approved,
    EventStatus.Online,
  ];
  const canCancelNow =
    !!event &&
    CANCELLABLE_STATUSES.includes(event.status) &&
    (isCentral || (event.startAt ?? '').slice(0, 10) >= today);
  const canCompleteNow =
    !!event &&
    (event.status === EventStatus.Approved || event.status === EventStatus.Online) &&
    (event.endAt ?? '').slice(0, 10) <= today;

  const isHostedByCentralType = (vaType: string) =>
    eventTypes.find((et) => et.id === vaType)?.hostedByCentral === true;

  // Submitting hands the event over: from in_review on, a depot-scoped role can
  // only look, and getting it back means asking central to reject it. Mirrors
  // canUpdateEvent() in the backend, which is the authority for the status/depot
  // checks — this only keeps the form from offering edits that the server would
  // refuse.
  //
  // Zentralseitige VAs (22.09.2026-Follow-up, 24.09.2026 auf hostedByCentral
  // verfeinert): a partner may not edit an event Zentrale created ONLY when the
  // referenced VA-Typ is itself `hostedByCentral` — a Central-origin event on a
  // regular VA-Typ, assigned to their own depot, must stay editable while
  // `initial`/`rejected` (bugfix: the previous version blocked every
  // central-origin event regardless of VA-Typ). This one clause is
  // FRONTEND-ONLY by decision, unlike the rest of this check —
  // canUpdateEvent()/canUpdateQtyOnly() in the backend do not look at
  // `origin`/`vaType`, so a direct API call still succeeds. The qty exception
  // below deliberately still applies here too (kept changeable either way).
  // Tracked separately so the frozen-notice banner below can name the actual
  // reason.
  const frozenDueToOrigin =
    isPartner &&
    !!event &&
    event.origin === EventOrigin.Central &&
    isHostedByCentralType(event.vaType);
  const frozenForPartner =
    isPartner &&
    !!event &&
    // Rejected is the same level as Initial (24.09.2026): still editable.
    ((event.status !== EventStatus.Initial && event.status !== EventStatus.Rejected) ||
      frozenDueToOrigin);
  // A closed-out event stays frozen for EVERY role, not just partners
  // (22.09.2026-Follow-up) — editing anything else on it makes no sense once
  // it has happened, but the contingent still needs to move (see qtyOnlyMode
  // below): tickets can still be corrected/reconciled during the Abrechnung.
  const isCompletedEvent = !!event && event.status === EventStatus.Completed;
  const readOnly = mode === 'review' || frozenForPartner || isCompletedEvent;

  // Altdaten aus dem alten six-cms-Weg (DCP-2228 follow-up, 18.09.2026) —
  // "alles außer v2" gilt bereits jetzt als Altdaten, nicht erst nach der
  // geplanten data-sync-Migration, die bestehende Objekte formal mit `v1`
  // markiert (Nutzerentscheidung). Für solche VAs darf über dieses Tool kein
  // neues Kontingent mehr entstehen, sobald sie freigegeben sind — die
  // frozenDueToOrigin-Ausnahme (Kontingent bleibt änderbar) ergibt für
  // Altdaten keinen Sinn und würde Partnern fälschlich eine Möglichkeit
  // zeigen, die es hier nicht geben soll.
  const isLegacyApprovedOrLater =
    !!event &&
    event.eventsVersion !== 'v2' &&
    (event.status === EventStatus.Approved ||
      event.status === EventStatus.Online ||
      event.status === EventStatus.Completed);

  // The one exception that survives the freeze — for a submitted-but-not-yet-
  // completed partner event AND for any completed event, regardless of role.
  // Seats keep selling/need correcting, so the organiser must be able to adjust
  // the quantity without a round trip through central (or, once completed, at
  // all — canUpdateEvent()/canUpdateQtyOnly() in the backend never blocked this
  // on status; only this form's Save button used to disappear).
  const qtyOnlyMode = (frozenForPartner || isCompletedEvent) && mode !== 'review' && !isLegacyApprovedOrLater;
  const qtyEditable = !readOnly || qtyOnlyMode;

  // Depots the partner is allowed to manage as origin (their assigned depots).
  const ownDepotKeys = new Set(userDepots.map((d) => d.key));
  // Origin depots already on the event that the partner may NOT touch (e.g. central-created).
  // These are rendered locked (read-only chips) rather than hidden, so context stays visible.
  const lockedOriginKeys = isPartner
    ? form.depots.filter((d) => !ownDepotKeys.has(d.key)).map((d) => d.key)
    : [];

  // "Vorschau"-Button (24.09.2026, Nutzervorgabe) — erst aktiv, wenn die VA
  // gespeichert ist (event.identifier + slug existieren) UND der letzte
  // validate()-Lauf keine Fehler hinterlassen hat. `errors` ist wie überall
  // sonst in diesem Formular nur so aktuell wie der letzte Speicher-/
  // Einreichversuch — dieselbe Konvention wie beim Tab-Sprung oben.
  const previewEnabled = !!event?.identifier && !!event?.slug && Object.keys(errors).length === 0;
  const previewUrl =
    event?.slug && STOREFRONT_BASE_URL ? `${STOREFRONT_BASE_URL}/event/${event.slug}` : undefined;

  // "Serie erstellen" (24.09.2026, Nutzervorgabe) — erst aktiv, wenn die VA
  // gespeichert ist und fehlerfrei validiert wurde (gleiche Konvention wie
  // previewEnabled oben), UND nicht im Status "Nicht freigegeben" (die
  // einzige ausgeschlossene Ausgangsbasis laut Nutzervorgabe).
  const seriesEnabled =
    !!event?.identifier &&
    Object.keys(errors).length === 0 &&
    event.status !== EventStatus.Rejected;
  const [seriesDialogOpen, setSeriesDialogOpen] = useState(false);

  // "Stattgefunden markieren"/"Stornieren"/"Löschen" (25.09.2026,
  // Nutzervorgabe) — in den Kopfbereich verschoben, weg von der vormaligen
  // Footer-Branch-Struktur. Bereits vollständig gegated (canCancelNow/
  // canCompleteNow bzw. die vom Elternteil übergebene, serverseitig
  // gespiegelte onDelete-Regel), daher hier nur noch die reine
  // Klick-Handler-Verdrahtung.
  const headerCancelEventClick =
    canCancelNow && onCancelEvent ? () => setConfirmCancelEvent(true) : undefined;
  const headerCompleteEventClick =
    canCompleteNow && onCompleteEvent ? () => setConfirmCompleteEvent(true) : undefined;

  useEffect(() => {
    if (!open) return;
    if (event) {
      // Onlinestellung beim Öffnen eines noch nicht eingereichten Entwurfs korrigieren
      // (Nutzervorgabe, 22.09.2026): zwischen Anlage und erneutem Öffnen kann Zeit
      // vergehen, sodass der damals gesetzte Montag inzwischen in der Vergangenheit
      // liegt. Dasselbe Muster wie handleSubmitForReview weiter unten, nur schon beim
      // Öffnen statt erst beim Einreichen — nur für Erstellung, nicht für bereits
      // eingereichte/genehmigte VAs, deren Onlinestellung absichtlich in der
      // Vergangenheit liegen kann (z. B. bereits veröffentlicht).
      let next = toForm(event);
      if (
        (event.status === EventStatus.Initial || event.status === EventStatus.Rejected) &&
        next.plannedOnlineDate &&
        dateOnlyPart(next.plannedOnlineDate) < todayDateOnly()
      ) {
        next = { ...next, plannedOnlineDate: combineDateTime(nextMondayOrToday(), '') };
      }
      setForm(next);
      setStartTimeText(splitDateTime(next.startAt).time);
      setEndTimeText(splitDateTime(next.endAt).time);
    }
    else if (copySource) {
      const next = toCopyForm(copySource);
      setForm(next);
      setStartTimeText(splitDateTime(next.startAt).time);
      setEndTimeText(splitDateTime(next.endAt).time);
    }
    else {
      const next = emptyForm(role, userDepots);
      setForm(next);
      setStartTimeText(splitDateTime(next.startAt).time);
      setEndTimeText(splitDateTime(next.endAt).time);
    }
    setErrors({});
    setSlugTouched(false);
    setSlugConflict(false);
    setConfirmReject(false);
    setRejectReason('');
    // Depot-Partner mit noch änderbarem Kontingent (in_review oder später,
    // aber vor dem Punkt, an dem auch das Kontingent einfriert) springt
    // direkt zum Tab "Teilnehmermanagement" — dort ist ohnehin nur noch das
    // Kontingent editierbar (Nutzervorgabe, 18.09.2026). qtyOnlyMode trifft
    // exakt diese Bedingung schon (inkl. Ausschluss der Altdaten-Regel).
    setActiveTab(isPartner && qtyOnlyMode ? 3 : 1);
    setPendingPreset(null);
    setPendingCancellationTerm(null);
    setPendingPaymentNoteTemplate(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event?.identifier, copySource?.identifier]);

  /**
   * Kategorie aus der gespeicherten Vorlage ableiten, damit das Vorlagenfeld
   * beim Öffnen einer bestehenden VA nicht leer und gesperrt dasteht.
   *
   * Bewusst ein EIGENER Effekt, getrennt vom Reset oben (Bug fix 20.09.2026):
   * `eventTexts` lädt asynchron und kann NACH dem Öffnen noch nachträglich
   * eintreffen (neue Referenz). Solange diese Ableitung im selben Effekt wie
   * der komplette Formular-Reset stand, hing `eventTexts` in DESSEN
   * Abhängigkeitsliste — jedes Nachladen setzte das ganze Formular ein
   * zweites Mal zurück und verschluckte, was der Nutzer in der Zwischenzeit
   * schon eingegeben hatte (z. B. gerade erst gewählte Uhrzeit bei einer
   * neuen VA — bei einer bestehenden VA fiel das nicht auf, weil dort
   * `eventTexts` beim Öffnen meist schon geladen war).
   */
  useEffect(() => {
    if (!open) return;
    const gespeichert = event?.presetTextId
      ? eventTexts.find((text) => text.id === event.presetTextId)
      : undefined;
    setPresetCategory(gespeichert?.category ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event?.identifier, eventTexts]);

  // Default "alle Verkaufskanäle aktiv" bei Neuanlage UND beim Kopieren —
  // emptyForm() selbst kann das nicht setzen, die Kanalliste kommt erst
  // asynchron über useSalesChannels() herein. `undefined` ist der Sentinel
  // für "noch nicht befüllt": die Checkbox-Handler unten setzen bei jeder
  // Bedienung immer ein Array (auch ein leeres, wenn alles abgewählt wird),
  // nie zurück auf undefined — ein einmal manuell geändertes Ergebnis wird
  // also nie wieder überschrieben.
  //
  // Bugfix 12.10.2026: `copySource` war hier bisher ausgeschlossen ("echte
  // Neuanlage"). salesChannels liegt aber NIE auf dem commercetools-Objekt
  // selbst, sondern kommt beim Lesen nur vom ticket-api-Kontingent, und nur
  // wenn `event.ticketed === true` ist (withAvailable(), available.ts) — eine
  // noch nicht "ticketed" VA liefert also bereits vom Server `undefined`.
  // toCopyForm() übernimmt diesen undefined unverändert, und ohne die
  // Ausnahme hier blieb er es auch: eine Kopie einer solchen VA hatte dann
  // KEINEN aktiven Verkaufskanal, bei Partnern unsichtbar (Feld ausgeblendet,
  // siehe isCentral unten) und bei qty>0 zusätzlich einen für Partner nicht
  // erreichbaren Validierungsfehler. Ungefährlich für die Kopie einer bereits
  // "ticketed" VA mit echter Kanalauswahl: der innere Check (`f.salesChannels
  // === undefined`) überschreibt eine vererbte, tatsächlich gesetzte Auswahl
  // ohnehin nie.
  useEffect(() => {
    if (!open || event || salesChannels.length === 0) return;
    setForm((f) =>
      f.salesChannels === undefined
        ? { ...f, salesChannels: salesChannels.map((sc) => sc.id) }
        : f
    );
  }, [open, event, salesChannels]);

  // Default-Vorlage für die Zahlart "Im Depot oder per Überweisung" bei echter
  // Neuanlage (24.09.2026, Nutzervorgabe) — gleiches Sentinel-Muster wie bei
  // salesChannels oben: emptyForm() setzt schon paymentMethod, aber die
  // passende Vorlage kommt erst asynchron über usePaymentNoteTemplates()
  // herein. `paymentNoteTemplateId === undefined` heißt "noch nie gesetzt" —
  // die Zahlart-Auswahl weiter unten setzt bei jeder Bedienung immer einen
  // Wert (auch `undefined` explizit bei Individuellem Text, siehe dort), aber
  // das passiert über set(), nicht über diesen Sentinel-Check, also
  // überschreibt dieser Effekt eine bereits getroffene Nutzerwahl nie.
  useEffect(() => {
    if (!open || event || copySource) return;
    if (form.paymentMethod !== 'depot_or_transfer' || form.paymentNoteTemplateId !== undefined) return;
    const vorlage = paymentNoteTemplates.find((t) => t.paymentMethod === 'depot_or_transfer');
    if (!vorlage) return;
    setForm((f) =>
      f.paymentNoteTemplateId === undefined
        ? { ...f, paymentNoteTemplateId: vorlage.id, paymentNote: vorlage.text }
        : f
    );
  }, [open, event, copySource, form.paymentMethod, form.paymentNoteTemplateId, paymentNoteTemplates]);

  // "Sprechende URL": Live-Vorschau aus Titel+Datum (DCP-2228 follow-up,
  // 18.09.2026). Bug fix 18.09.2026: solange der SERVER noch keinen Slug für
  // diese VA kennt (`event?.slug` leer — neu angelegt, kopiert, oder eine
  // Altlast ohne Slug), bleibt der Vorschlag live an Titel/Datum gekoppelt,
  // auch nachdem er einmal entstanden ist — sonst zeigte ein Tippfehler im
  // Titel oder ein falsch gewähltes Datum vor dem allerersten Speichern nie
  // die Korrektur. `slugTouched` schaltet das ab, sobald Admin das Feld
  // selbst editiert (siehe die Checkbox-Handler unten) — von da an übernimmt
  // der Mensch die Kontrolle. Ein bereits GESPEICHERTER Slug (`event.slug`
  // gesetzt) wird dagegen nie automatisch angefasst, sonst brächen bereits
  // geteilte/indexierte Links bei jeder späteren Titel-Korrektur.
  useEffect(() => {
    if (!open || slugTouched || event?.slug) return;
    setForm((f) => {
      const suggestion = buildSlugSuggestion(f.title, f.startAt, f.endAt);
      return suggestion && suggestion !== f.slug ? { ...f, slug: suggestion } : f;
    });
  }, [open, event?.slug, slugTouched, form.title, form.startAt, form.endAt, form.slug]);

  // Live-Muster-Prüfung der Sprechenden URL (DCP-2228 follow-up, 18.09.2026,
  // Nutzerwunsch): der Fehler soll schon direkt bei der Live-Vorschau
  // sichtbar sein, nicht erst beim Speichern — z. B. wenn der Titel Umlaute
  // enthält, kann der Vorschlag von Anfang an ungültig sein, und wer den
  // Slug nicht selbst editieren darf (Partner/Zentrale), soll das sofort
  // sehen, um den Titel anzupassen. Läuft unabhängig von validate() (das
  // dieselbe Prüfung zusätzlich beim Speichern wiederholt), damit die
  // Meldung sich sofort löst, sobald der Wert wieder passt.
  useEffect(() => {
    if (!open) return;
    const message =
      form.slug && !/^[a-zA-Z0-9_-]{2,256}$/.test(form.slug) ? t('form.errors.slugPattern') : undefined;
    setErrors((prev) => {
      if ((prev.slug ?? '') === (message ?? '')) return prev;
      const next = { ...prev };
      if (message) next.slug = message;
      else delete next.slug;
      return next;
    });
  }, [open, form.slug, t]);

  // Live-Verfügbarkeits-Hinweis (DCP-2228 follow-up, 18.09.2026, Nutzerwunsch):
  // fragt best-effort nach, ob der aktuelle Slug an einem der gewählten Depots
  // schon vergeben ist, statt das erst beim Speichern per 409 zu erfahren.
  // Wiederverwendet die bestehende Listing-Route (GET /api/events) — dieselbe
  // Sicht, die die VA-Übersicht auch hat, kein neuer Endpunkt nötig. Bleibt ein
  // reiner Hinweis: die serverseitige Prüfung in CtEventRepository.ts
  // (resolveSlug) ist und bleibt die eigentliche Instanz beim Speichern.
  useEffect(() => {
    if (!open || !form.slug || errors.slug || form.depots.length === 0) {
      setSlugConflict(false);
      return;
    }

    const depotKeys = form.depots.map((d) => d.key);
    const slug = form.slug;
    const ownIdentifier = event?.identifier;
    let cancelled = false;

    const timer = setTimeout(() => {
      void apiClient
        // pageSize ist serverseitig auf 100 gedeckelt (parsePagination,
        // shared.ts) — 200 warf bislang ein 400 INVALID_PAGE_SIZE, wodurch
        // der Live-Hinweis nie ankam (Bug gefunden 18.09.2026). Bleibt ein
        // Best-Effort-Hinweis: die serverseitige Prüfung beim Speichern
        // (CtEventRepository.ts's resolveSlug()) deckelt ohnehin nicht.
        .getEvents({ depotKeys, pageSize: 100 })
        .then((result) => {
          if (cancelled) return;
          setSlugConflict(
            result.items.some((e) => e.identifier !== ownIdentifier && e.slug === slug)
          );
        })
        .catch(() => {
          // Best-effort — die serverseitige Prüfung beim Speichern greift ohnehin.
        });
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, form.slug, form.depots, errors.slug, event?.identifier]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const start = splitDateTime(form.startAt);
  const end = splitDateTime(form.endAt);

  // validate() — seam for future zod migration.
  /**
   * `target` optional (default: der aktuelle form-State) — der Resubmit-
   * Plausibilitätscheck für plannedOnlineDate (handleSubmitForReview) prüft
   * eine bereits korrigierte Kopie, bevor sie per setForm im echten State
   * landet; setState ist async, ein zweiter Aufruf ohne Parameter würde in
   * derselben Funktion noch den alten Wert sehen (Nutzervorgabe, 18.09.2026).
   *
   * PURE — setzt selbst KEINE errors (anders als validate() unten). Getrennt
   * seit 24.09.2026 (Bugfix): handleSaveAndContinue braucht das volle
   * Fehlerobjekt nur, um es auf den aktuellen Tab zu filtern (nur der
   * aktuelle Tab wird bei "Speichern & weiter" geprüft/angezeigt) — ein Fehler
   * auf einem noch gar nicht besuchten Tab (z. B. Kostenbeitrag auf Tab 3)
   * darf dabei weder blockieren noch angezeigt werden. validate() unten ist
   * für die "finalen" Aktionen (Speichern auf dem letzten Tab, Zur Prüfung,
   * Statuswechsel), die wirklich das gesamte Formular prüfen und anzeigen
   * müssen.
   */
  function computeErrors(target: FormState = form): Record<string, string> {
    const e: Record<string, string> = {};
    // Geschlossene Gesellschaft blendet Slug, Bewerbung, Onlinestellung, Anmeldung,
    // Zahlung und Bankverbindung aus — ein Fehler auf einem unsichtbaren Feld
    // ließe "Speichern" scheinbar wirkungslos (kein sichtbarer Fehler), daher
    // werden diese Felder dort nicht geprüft.
    const closed = target.audience === EventAudience.Closed;
    if (!target.title.trim()) e.title = t('form.errors.titleRequired');
    else if (target.title.trim().length > 60) e.title = t('form.errors.titleTooLong');
    if (!target.vaType) e.vaType = t('form.errors.vaTypeRequired');
    if (target.depots.length === 0) e.depots = t('form.errors.depotRequired');
    // Bewerbendes Depot Pflichtfeld (24.09.2026, Nutzervorgabe).
    if (!closed && target.promotingDepots.length === 0) {
      e.promotingDepots = t('form.errors.promotingDepotRequired');
    }
    if (!target.audience) e.audience = t('form.errors.audienceRequired');
    // Nur ein clientseitiger Vor-Check (DCP-2228 follow-up, 18.09.2026) — die
    // Eindeutigkeit pro Depot prüft ausschließlich der Server, siehe
    // CtEventRepository.ts's resolveSlug().
    if (!closed && target.slug && !/^[a-zA-Z0-9_-]{2,256}$/.test(target.slug)) {
      e.slug = t('form.errors.slugPattern');
    }

    // Partner/Mitarbeiter may only use their OWN depots as origin (except pre-existing locked ones).
    // SEAM: authoritative enforcement is server-side (JWT depots claim) — this is a client-side guard.
    if (isPartner) {
      const lockedSet = new Set(lockedOriginKeys);
      const illegal = target.depots.filter(
        (d) => !ownDepotKeys.has(d.key) && !lockedSet.has(d.key)
      );
      if (illegal.length > 0) {
        e.depots = t('form.errors.depotNotOwned', { keys: illegal.map((d) => d.key).join(', ') });
      }
    }

    if (!target.startAt) e.startAt = t('form.errors.startRequired');
    if (!target.endAt) e.endAt = t('form.errors.endRequired');
    if (target.startAt && target.endAt && new Date(target.endAt) < new Date(target.startAt)) {
      e.endAt = t('form.errors.endBeforeStart');
    }
    // Partner darf keine VA in der Vergangenheit anlegen (29.09.2026,
    // Nutzervorgabe) — nur für Depot-Rollen, Zentrale/Admin bleiben wie
    // gewohnt unbeschränkt (gleiche Asymmetrie wie bei plannedOnlineDate
    // weiter unten). `!e.endAt` lässt die aussagekräftigere endBeforeStart-
    // Meldung stehen, statt sie zu überschreiben.
    if (isPartner) {
      if (target.startAt && dateOnlyPart(target.startAt) < todayDateOnly()) {
        e.startAt = t('form.errors.startInPast');
      }
      if (target.endAt && !e.endAt && dateOnlyPart(target.endAt) < todayDateOnly()) {
        e.endAt = t('form.errors.endInPast');
      }
    }
    // Uhrzeit oder Geschäftszeiten, eins von beiden (22.09.2026-Follow-up): ohne
    // diese Prüfung ließ combineDateTime() ein leeres Zeitfeld klaglos zu
    // UTC-Mitternacht werden (siehe combineDateTime's `!time`-Zweig) — eine VA
    // "ohne Uhrzeit" sah dann aus wie eine VA um Mitternacht, statt einen Fehler
    // zu zeigen.
    //
    // Geprüft wird bewusst gegen startTimeText/endTimeText (Closure, immer der
    // sichtbare Eingabewert), NICHT gegen start.time/end.time (Bugfix,
    // 25.09.2026): Letztere leiten sich aus form.startAt/endAt ab, das die
    // Zeit-Inputs unten nur bei einer TIME_PATTERN-gültigen Eingabe
    // überschreiben — ein nachträglich geleertes oder ungültiges Feld (z. B.
    // "67:35") ließ form.startAt/endAt unverändert auf dem letzten gültigen
    // Wert stehen, sodass diese Prüfung nie etwas bemerkte.
    if (!target.businessHours) {
      if (!startTimeText || !endTimeText) {
        e.time = t('form.errors.timeRequired');
      } else if (!TIME_PATTERN.test(startTimeText) || !TIME_PATTERN.test(endTimeText)) {
        e.time = t('form.errors.timeInvalid');
      }
    }
    if (target.costEnabled && (target.priceAmount == null || target.priceAmount < 0)) {
      e.priceAmount = t('form.errors.priceRequired');
    }

    if (closed) {
      // nicht angezeigt, nicht geprüft
    } else if (!target.plannedOnlineDate) {
      e.plannedOnlineDate = t('form.errors.plannedOnlineDateRequired');
    } else if (isPartner && dateOnlyPart(target.plannedOnlineDate) < todayDateOnly()) {
      // Nur für Partner/Mitarbeiter (29.09.2026, Nutzervorgabe) — Zentrale/Admin
      // dürfen weiterhin ein Onlinestellungsdatum in der Vergangenheit setzen.
      e.plannedOnlineDate = t('form.errors.plannedOnlineDateInPast');
    } else if (
      isPartner &&
      dateOnlyPart(target.plannedOnlineDate) !== dateOnlyPart(event?.plannedOnlineDate) &&
      !isMonday(target.plannedOnlineDate)
    ) {
      // Nur Montage für Partner/Mitarbeiter (03.11.2026, Nutzervorgabe). Geprüft wird
      // nur, wenn das Datum von dem gespeicherten abweicht — eine VA, die die Zentrale
      // oder der Import auf einen anderen Wochentag gelegt hat, bleibt sonst speicherbar.
      // Die native Datumsauswahl lässt sich nicht auf Montage einschränken, deshalb
      // greift die Prüfung bei der Eingabe; der Server prüft dieselbe Regel.
      e.plannedOnlineDate = t('form.errors.plannedOnlineDateNotMonday');
    }

    const descriptionLength = plainTextLength(target.description);
    if (descriptionLength === 0) {
      e.description = t('form.errors.descriptionRequired');
    } else if (descriptionLength < 250) {
      e.description = t('form.errors.descriptionTooShort');
    } else if (descriptionLength > 1500) {
      e.description = t('form.errors.descriptionTooLong');
    }

    if (target.costEnabled && isBlankMarkdown(target.paymentNote)) {
      e.paymentNote = t('form.errors.paymentNoteRequired');
    }
    if (!closed && target.glassOrTableSale === undefined) {
      e.glassOrTableSale = t('form.errors.glassOrTableSaleRequired');
    }
    if (closed) {
      // Anmeldung ausgeblendet
    } else if (target.registrationRequired && !target.registrationDeadline) {
      e.registrationDeadline = t('form.errors.registrationDeadlineRequired');
    } else if (
      target.registrationRequired &&
      target.registrationDeadline &&
      target.plannedOnlineDate &&
      dateOnlyPart(target.registrationDeadline) < dateOnlyPart(target.plannedOnlineDate)
    ) {
      // Fristen dürfen nicht vor der Onlinestellung liegen (24.09.2026,
      // Nutzervorgabe) — bis dahin ist die VA gar nicht sichtbar, eine
      // frühere Frist könnte also nie greifen.
      e.registrationDeadline = t('form.errors.deadlineBeforeOnlineDate');
    } else if (
      isPartner &&
      target.registrationRequired &&
      target.registrationDeadline &&
      dateOnlyPart(target.registrationDeadline) < todayDateOnly()
    ) {
      // Zusätzlich zur Prüfung gegen plannedOnlineDate oben (29.09.2026,
      // Nutzervorgabe) — die allein hätte ein bereits in der Vergangenheit
      // liegendes plannedOnlineDate (z. B. von der Zentrale vor dieser Regel
      // gesetzt) nicht bemerkt. Nur für Partner/Mitarbeiter.
      e.registrationDeadline = t('form.errors.deadlineInPast');
    }
    if (closed) {
      // Zahlung ausgeblendet
    } else if (target.costEnabled && !target.paymentDeadline) {
      e.paymentDeadline = t('form.errors.paymentDeadlineRequired');
    } else if (
      target.costEnabled &&
      target.paymentDeadline &&
      target.plannedOnlineDate &&
      dateOnlyPart(target.paymentDeadline) < dateOnlyPart(target.plannedOnlineDate)
    ) {
      e.paymentDeadline = t('form.errors.deadlineBeforeOnlineDate');
    } else if (
      isPartner &&
      target.costEnabled &&
      target.paymentDeadline &&
      dateOnlyPart(target.paymentDeadline) < todayDateOnly()
    ) {
      e.paymentDeadline = t('form.errors.deadlineInPast');
    }

    if (target.locationType === 'external') {
      if (!target.externalName?.trim()) e.externalName = t('form.errors.externalNameRequired');
      if (!target.externalStreet?.trim()) e.externalStreet = t('form.errors.externalStreetRequired');
      if (!target.externalHouseNo?.trim()) e.externalHouseNo = t('form.errors.externalHouseNoRequired');
      if (!target.externalZip?.trim()) e.externalZip = t('form.errors.externalZipRequired');
      if (!target.externalCity?.trim()) e.externalCity = t('form.errors.externalCityRequired');
      if (!target.externalCountry?.trim()) e.externalCountry = t('form.errors.externalCountryRequired');
    }

    // Verfügbare Plätze: nur Pflicht, wenn überhaupt angemeldet UND bezahlt wird —
    // eine kostenfreie Anmeldung braucht kein festes Kontingent.
    if (target.registrationRequired && target.costEnabled && target.qty == null) {
      e.qty = t('form.errors.qtyRequired');
    }

    // Belegte Plätze (reserviert + verkauft) dürfen nicht unterschritten werden.
    // Der Server prüft das ebenfalls (409); hier nur, um die Rückfahrt zu sparen.
    const committed = committedSeats(event);
    if (committed > 0 && target.qty != null && target.qty < committed) {
      e.qty = t('form.errors.qtyBelowSold', { sold: committed });
    }

    // A quantity without a channel would create a contingent nobody can buy from.
    // The backend refuses it too (400), but catching it here points at the field.
    if ((target.qty ?? 0) > 0 && (target.salesChannels?.length ?? 0) === 0) {
      e.salesChannels = t('form.errors.salesChannelRequired');
    }

    // Kontoinhaber/IBAN/BIC/Verwendungszweck: Pflicht, sobald die Bankverbindung
    // überhaupt angezeigt wird — nicht mehr an paymentMethod gekoppelt
    // (Nutzervorgabe, 18.09.2026: vorher nur bei "Nur Überweisung" Pflicht).
    // Ausgenommen, wenn die Felder gar nicht angezeigt werden
    // (publishAccountData aus) — ein ausgeblendetes Feld kann niemand ausfüllen,
    // dieselbe Konvention wie bei externalName oben.
    const bankDetailsRequired = !closed && (target.publishAccountData ?? true);
    if (bankDetailsRequired && !target.paymentAccountHolder?.trim()) {
      e.paymentAccountHolder = t('form.errors.paymentAccountHolderRequired');
    }
    if (bankDetailsRequired && !target.paymentReference?.trim()) {
      e.paymentReference = t('form.errors.paymentReferenceRequired');
    }

    // Beide Felder sonst optional — nur bei einer Eingabe wird das Format geprüft. Die
    // IBAN trägt eine Prüfziffer (isValidIban prüft sie mit), die BIC nicht (siehe
    // bank-validation.ts für die Begründung).
    if (bankDetailsRequired && !target.paymentIban?.trim()) {
      e.paymentIban = t('form.errors.paymentIbanRequired');
    } else if (!closed && target.paymentIban?.trim() && !isValidIban(target.paymentIban)) {
      e.paymentIban = t('form.errors.ibanInvalid');
    }
    if (bankDetailsRequired && !target.paymentBic?.trim()) {
      e.paymentBic = t('form.errors.paymentBicRequired');
    } else if (!closed && target.paymentBic?.trim() && !isValidBic(target.paymentBic)) {
      e.paymentBic = t('form.errors.bicInvalid');
    }

    return e;
  }

  /**
   * Prüft und zeigt das GESAMTE Formular an (setErrors mit allen Feldern) —
   * für die finalen Aktionen: "Speichern" auf dem letzten Tab, "Zur Prüfung",
   * und jeden Statuswechsel (approve/reject/reopen/cancel/complete über
   * pendingSavePayload). "Speichern & weiter" nutzt bewusst computeErrors()
   * direkt, nicht diese Funktion — siehe dessen Kommentar.
   */
  function validate(target: FormState = form): Record<string, string> {
    const e = computeErrors(target);
    setErrors(e);
    return e;
  }

  /**
   * Feldweise Prüfung bei Verlassen des Feldes (24.09.2026, Nutzervorgabe) —
   * EIN delegierter onBlur auf dem Tab-Inhalt (statt an jedem einzelnen Feld),
   * weil React's onBlur intern auf `focusout` läuft und damit bubbelt: das
   * Ereignis wird per closest('[data-field]') dem Field-Wrapper zugeordnet,
   * der das genutzte data-field-Attribut trägt (siehe Field-Komponente unten).
   * Ändert NUR den Fehler für genau dieses eine Feld — anders als
   * handleSaveAndContinue (ersetzt die ganze Tab-Fehlermenge) wird hier
   * gemerged, damit ein bereits laufender Tab-Check nicht durch das
   * Weiterklicken in ein anderes Feld wieder verschwindet. Setzt den Fehler,
   * wenn das Feld ungültig ist, und löscht ihn wieder, sobald es gültig wird.
   */
  /**
   * Setzt oder löscht den Fehler für GENAU ein Feld und lässt alle anderen stehen —
   * gemeinsame Grundlage für die Prüfung beim Verlassen eines Feldes und für die
   * Prüfung direkt bei der Auswahl (Datumsfeld, siehe unten).
   */
  const mergeFieldError = (fieldName: string, message: string | undefined) => {
    setErrors((prev) => {
      if (message) {
        if (prev[fieldName] === message) return prev;
        return { ...prev, [fieldName]: message };
      }
      if (!(fieldName in prev)) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const handleFieldBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const fieldName = (e.target as HTMLElement).closest('[data-field]')?.getAttribute('data-field');
    if (!fieldName) return;
    mergeFieldError(fieldName, computeErrors(form)[fieldName]);
  };

  /**
   * Geplante Onlinestellung: prüft schon bei der Auswahl im Kalender (03.11.2026,
   * Nutzervorgabe), nicht erst beim Verlassen des Feldes — nach einem Klick im
   * Datumsfenster bleibt der Fokus im Feld, ein blur kommt also nicht.
   *
   * `value` kommt als Parameter, weil der State erst nach dem Render den neuen Wert
   * trägt. Zwischenstände beim Tippen (leer, Jahr unter 1000 wie 0002-…) werden
   * übergangen: das Feld meldet sie als gültige Werte, sie würden die Meldung nur
   * aufblitzen lassen. Die endgültige Prüfung bleibt beim Speichern.
   */
  const checkOnlineDateOnPick = (value: string) => {
    const day = dateOnlyPart(value);
    if (!day || Number(day.slice(0, 4)) < 1000) return;
    mergeFieldError('plannedOnlineDate', computeErrors({ ...form, plannedOnlineDate: value }).plannedOnlineDate);
  };

  // Zum ersten gefundenen Fehler springen (Tab wechseln, dann zum Feld
  // scrollen) — zwei Effekte statt einem: der Tab-Wechsel muss erst rendern,
  // bevor das Feld des Zielfelds überhaupt im DOM steht und angesprungen
  // werden kann. Object.keys() liefert die Felder in der Reihenfolge, in der
  // sie in validate() eingetragen wurden, also in Formular-Lesereihenfolge.
  // Greift für jede Fehlerquelle gleich, nicht nur validate() selbst — auch
  // die reinen qty-Prüfungen im gesperrten Partner-Fall (siehe handleSave/
  // pendingSavePayload) landen in `errors` und lösen denselben Sprung aus.
  //
  // Hängt bewusst an errorJumpTrigger, NICHT an errors selbst (24.09.2026,
  // Bugfix — siehe der State-Kommentar oben): sonst würde JEDE errors-Änderung
  // springen, auch handleSaveAndContinues rein lesender validate()-Aufruf.
  useEffect(() => {
    const firstErrorField = Object.keys(errors)[0];
    const tab = firstErrorField ? ERROR_FIELD_TAB[firstErrorField] : undefined;
    if (tab && tab !== activeTab) setActiveTab(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorJumpTrigger]);

  useEffect(() => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField) return;
    document
      .querySelector(`[data-field="${firstErrorField}"]`)
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [errors, activeTab]);

  const handleSave = () => {
    // A frozen event is still saveable for the one open field. Send qty ALONE:
    // the backend counts the payload keys and refuses anything beyond qty, so a
    // full form here would fail with 403 even if only the quantity changed.
    if (qtyOnlyMode) {
      // Only the qty rule applies here; the rest of the form is untouched, so
      // running full validation would block on unrelated pre-existing gaps.
      const committed = committedSeats(event);
      if (committed > 0 && form.qty != null && form.qty < committed) {
        setErrors({ qty: t('form.errors.qtyBelowSold', { sold: committed }) });
        setErrorJumpTrigger((n) => n + 1);
        return;
      }
      setErrors({});
      onSaveUpdate({ qty: form.qty });
      return;
    }

    if (readOnly) return;
    if (Object.keys(validate()).length > 0) {
      setErrorJumpTrigger((n) => n + 1);
      return;
    }
    if (mode === 'create') onSaveCreate(form);
    else onSaveUpdate(form);
  };

  /**
   * "Speichern & weiter" auf Tab 1/2 (Nutzervorgabe, 18.09.2026, verschärft
   * 24.09.2026) — prüft und zeigt NUR die Felder des AKTUELLEN Tabs, nicht
   * das ganze Formular: ein noch nicht besuchter späterer Tab (z. B. der
   * Kostenbeitrag auf Tab 3) darf beim Weiterblättern von Tab 1 zu Tab 2
   * weder blockieren noch dort schon als Fehler aufscheinen — sonst zeigt
   * Tab 2 nach dem Sprung sofort einen Fehler, den man auf Tab 3 gar nicht
   * gesucht hat, und der Blick für das, was auf DIESEM Tab noch fehlt, geht
   * verloren. Nutzt bewusst computeErrors() (pure) statt validate(): das volle
   * Fehlerobjekt wird nur berechnet, um es auf den aktuellen Tab zu filtern —
   * setErrors() bekommt danach ausschließlich diese gefilterte Teilmenge, alle
   * anderen Tabs bleiben ungeprüft/fehlerfrei angezeigt, bis tatsächlich
   * "Speichern"/"Zur Prüfung" auf dem letzten Tab das gesamte Formular prüft.
   *
   * Bei Neuanlage (mode==='create') gibt es serverseitig keinen Teil-Create —
   * ein POST verlangt das vollständige EventCreateInput samt aller
   * Cross-Field-Pflichten (z. B. priceAmount, das auf Tab 3 liegt), anders als
   * das PUT beim Bearbeiten. Für Neuanlage blättert dieser Button deshalb nur
   * lokal weiter; der eigentliche Anlegen-Call passiert erst auf dem letzten
   * Tab ("Speichern") oder über "Einreichen" von jedem Tab aus (beide senden
   * ohnehin das volle Formular). Beim Bearbeiten speichert er dagegen sofort
   * — ein PUT ist ein Teil-Update, andere Tabs bleiben serverseitig unberührt.
   */
  const handleSaveAndContinue = () => {
    const allErrors = computeErrors(form);
    const tabErrors = Object.fromEntries(
      Object.entries(allErrors).filter(([field]) => ERROR_FIELD_TAB[field] === activeTab)
    );
    setErrors(tabErrors);
    if (Object.keys(tabErrors).length > 0) return;

    if (mode !== 'create') onSaveUpdate(form, true);
    goToTab((activeTab + 1) as 1 | 2 | 3);
  };

  const handleSubmitForReview = () => {
    // Plausibilität der Onlinestellung kurz vor dem Einreichen erneut prüfen
    // (Nutzervorgabe, 18.09.2026): zwischen Anlage und Einreichen kann Zeit
    // vergehen, sodass der bei Anlage gesetzte Montag inzwischen in der
    // Vergangenheit liegt. Silently auf den nächsten Montag korrigieren,
    // VOR validate() — dessen eigene plannedOnlineDateInPast-Prüfung würde
    // sonst blockieren statt die Korrektur zuzulassen. validate() bekommt die
    // korrigierte Kopie direkt übergeben (setForm ist async, ein paralleler
    // Aufruf ohne Parameter sähe hier noch den alten Wert).
    let target = form;
    if (form.plannedOnlineDate && dateOnlyPart(form.plannedOnlineDate) < todayDateOnly()) {
      target = { ...form, plannedOnlineDate: combineDateTime(nextMondayOrToday(), '') };
      setForm(target);
    }

    if (Object.keys(validate(target)).length > 0) {
      setErrorJumpTrigger((n) => n + 1);
      return;
    }
    // create mode: there is nothing to submit yet — create it and submit the
    // newly created one in the same action. Edit/review mode: the event
    // already exists, submit it directly.
    if (mode === 'create') {
      onSubmitCreate(target);
      return;
    }
    onSubmit(target);
  };

  /**
   * What handleSave would persist right now, computed for every status
   * transition below (approve/reject/reopen/cancel/complete) — not just for
   * the explicit "Speichern" button.
   *
   * Bug fix 09.09.2026: a central reviewer's form stays fully editable while
   * an event is in_review (readOnly is false for them — see readOnly/
   * frozenForPartner above), so approving straight after editing a field used
   * to send only the status change and silently drop the edit. Every
   * transition must carry whatever is pending, exactly like a save would.
   *
   * Mirrors handleSave's own frozenForPartner/readOnly split: a frozen
   * partner form may only ever send `qty` (the backend rejects a fuller
   * payload with 403), and only that field's own rule is checked — running
   * full validate() there would block on unrelated, pre-existing gaps the
   * partner never touched. `false` means validation failed and the caller
   * must abort the transition; `undefined` means there is nothing pending to
   * save (mode === 'review') and the transition may proceed on its own.
   */
  function pendingSavePayload(): Partial<CreateEventRequest> | undefined | false {
    if (qtyOnlyMode) {
      const committed = committedSeats(event);
      if (committed > 0 && form.qty != null && form.qty < committed) {
        setErrors({ qty: t('form.errors.qtyBelowSold', { sold: committed }) });
        setErrorJumpTrigger((n) => n + 1);
        return false;
      }
      setErrors({});
      return { qty: form.qty };
    }
    if (readOnly) return undefined;
    if (Object.keys(validate()).length > 0) {
      setErrorJumpTrigger((n) => n + 1);
      return false;
    }
    return form;
  }

  /** Wraps approve/reopen/cancel/complete so they save whatever is pending first, or abort on invalid input. */
  function withSave(action: (payload?: Partial<CreateEventRequest>) => void) {
    return () => {
      const payload = pendingSavePayload();
      if (payload === false) return;
      action(payload);
    };
  }

  const depotOriginOptions = isCentral ? allDepots : userDepots;

  /**
   * Fills in depot names from the depot list.
   *
   * A stored event keeps the name alongside the key, but records created before
   * that — and the depots in the auth claim — carry only the key. Without this
   * the chips in an older event would read "D001" while the picker below shows
   * "D001 — Düsseldorf Lohausen".
   */
  const withNames = (refs: DepotRef[]) => {
    if (allDepots.length === 0) return refs;
    const byKey = new Map(allDepots.map((d) => [d.key, d]));
    return refs.map((d) => (d.name ? d : byKey.get(d.key) ?? d));
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* Kopf (Titel/Tabs) und Fußzeile bleiben fixiert, nur der Content
            dazwischen scrollt (Nutzervorgabe, 18.09.2026) — DialogContent ist
            Radix-Primitive mit eigenen grid/p-6-Basisklassen; cn() nutzt
            tailwind-merge, die neuen flex/p-0-Klassen hier überschreiben sie
            zuverlässig. gap-0 hebt die Basis-gap-4 auf, sonst klafft zwischen
            den drei Flex-Kindern (Kopf/Content/Fuß) ein unerwünschter Spalt.
            Feste Höhe statt max-h (12.10.2026, Nutzervorgabe) — der Dialog
            soll beim Tab-Wechsel nicht mehr springen, nur noch oben/unten
            etwas Abstand zum Viewport-Rand behalten. */}
        <DialogContent
          className="flex h-[90vh] max-w-[52.8rem] flex-col gap-0 overflow-hidden p-0"
          showCloseButton={false}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <div className="flex-shrink-0 space-y-4 p-6 pb-0">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <DialogTitle>
                {mode === 'create' ? t('form.createTitle') : mode === 'edit' ? t('form.editTitle') : t('form.reviewTitle')}
              </DialogTitle>
              {event && <StatusBadge status={event.status} />}
              {/* Status-Wechsel-Buttons auf Höhe des Titels (25.09.2026,
                  Nutzervorgabe) — vorher in der Tab-Zeile neben "Vorschau",
                  wo sie beim Scrollen/auf kleineren Bildschirmen aus dem
                  sichtbaren Bereich fielen. Gating unverändert, siehe
                  FooterActions-Kommentar. */}
              <div className="ml-auto flex gap-2">
                {headerCompleteEventClick && (
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-2 border-blue-50 bg-blue-50 text-blue-700 hover:border-blue-100 hover:bg-blue-100 hover:text-blue-800"
                    onClick={headerCompleteEventClick}
                  >
                    <Check className="h-4 w-4" />{t('actions.complete')}
                  </Button>
                )}
                {headerCancelEventClick && (
                  <Button
                    type="button"
                    variant="destructive"
                    className="gap-2"
                    onClick={headerCancelEventClick}
                  >
                    <X className="h-4 w-4" />{t('actions.cancelEvent')}
                  </Button>
                )}
                {onDelete && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="gap-2 text-destructive hover:text-destructive"
                    onClick={onDelete}
                  >
                    <Trash2 className="h-4 w-4" />{t('actions.delete')}
                  </Button>
                )}
              </div>
            </div>
            {/* Urheber nur an einer bestehenden Veranstaltung — beim Anlegen gibt es
                noch keinen, und der Server setzt ihn ohnehin (US-105). */}
            {event && (
              <p className="text-xs text-muted-foreground">
                {t('form.createdByLine', {
                  author: formatAuthor(event.createdBy),
                  role: event.createdByRole
                    ? tUsers(`switchRole.${event.createdByRole}`)
                    : t('form.createdByRoleUnknown'),
                })}
              </p>
            )}
          </DialogHeader>

          {event?.rejectionReason && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm">
              <span className="font-semibold text-red-700">{t('form.rejectionReasonLabel')}: </span>
              {event.rejectionReason}
            </div>
          )}

          {/* Says why the dates are empty — otherwise a prefilled form with two
              blank required fields looks like a loading glitch. */}
          {copySource && !event && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
              {t('form.copyHint')}
            </div>
          )}

          {/* Says why everything is greyed out — a form full of disabled inputs
              with no explanation reads as a bug. Two/three separate messages,
              not one shared by qtyOnlyMode (22.09.2026-Follow-up): the
              partner-freeze text points at asking central for a rejection,
              which makes no sense once the event has already happened, no
              sense for central itself, and no sense for a Central-origin
              event a partner never submitted in the first place. */}
          {/* Kein Hinweis mehr bei Altdaten ab "freigegeben" (DCP-2228
              follow-up, 18.09.2026) — es gibt dann keine Kontingent-Ausnahme
              mehr zu erklären, siehe isLegacyApprovedOrLater oben. */}
          {frozenForPartner && mode !== 'review' && !isLegacyApprovedOrLater && (
            <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{frozenDueToOrigin ? t('form.originFrozenNotice') : t('form.frozenNotice')}</span>
            </div>
          )}
          {isCompletedEvent && !frozenForPartner && mode !== 'review' && !isLegacyApprovedOrLater && (
            <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{t('form.completedNotice')}</span>
            </div>
          )}

          {/* Tabs (15.09.2026, Stepper-Optik 12.10.2026 nach Artifact-Vorlage) —
              kein Sequenzzwang: freies Hin- und Herspringen, alle drei Tabs
              teilen sich denselben form-State. Stepper ist rein
              präsentational, Navigation bleibt goToTab(). py-4 statt pb-3
              (Nutzervorgabe, 12.10.2026) — mehr Luft nach oben und unten. */}
          <div className="flex items-center gap-4 border-b py-4">
            <Stepper
              steps={([1, 2, 3] as const).map((n) => ({
                label: t(`form.tabs.tab${n}`),
                onClick: () => goToTab(n),
              }))}
              activeIndex={activeTab - 1}
            />
            {/* "Vorschau" (24.09.2026) — hellgrau (auch Rahmen) bis gespeichert+
                fehlerfrei, hellorange (auch Rahmen) sobald aktiv — gleiches Muster
                wie "Serie erstellen". Öffnet die Storefront-Ansicht in einem neuen
                Fenster/Tab. Tooltip bei inaktivem Zustand (25.09.2026, Nutzervorgabe). */}
            <Button
              type="button"
              variant="outline"
              className={
                'ml-auto gap-2 ' +
                (previewEnabled
                  ? 'border-orange-50 bg-orange-50 text-orange-700 hover:border-orange-100 hover:bg-orange-100 hover:text-orange-800'
                  : 'border-gray-100 bg-gray-100 text-muted-foreground hover:bg-gray-100')
              }
              disabled={!previewEnabled || !previewUrl}
              title={!previewEnabled || !previewUrl ? t('actions.previewBlocked') : undefined}
              onClick={() => previewUrl && window.open(previewUrl, '_blank', 'noopener,noreferrer')}
            >
              <ExternalLink className="h-4 w-4" />{t('actions.preview')}
            </Button>
          </div>
          </div>

          <div ref={contentRef} className="flex-1 overflow-y-auto p-6" onBlur={handleFieldBlur}>
          <div className="space-y-6">
            {activeTab === 1 && (
              <>
                {/* Ihre Veranstaltung */}
                <div className="space-y-3">
                  <SectionTitle>{t('form.sections.yourEvent')}</SectionTitle>

                  {/* Origin depot — single-select for every role (25.09.2026,
                      Nutzervorgabe): previously Zentrale/Admin could still pick
                      several via MultiDepotSelect (for a coming event type run
                      centrally across depots), but that is no longer the use
                      case, and the ticket-api's contingent now carries exactly
                      one depot_key — an array with more than one entry would be
                      ambiguous there. `depots` stays an array in the data model
                      in case multi-depot origin comes back; only the UI and the
                      server-side guard (assertSingleOriginDepot,
                      access-control.ts) now enforce "at most one" for everyone,
                      not just depot-scoped roles. */}
                  <Field
                    label={t('form.fields.originDepotSingle')}
                    error={errors.depots}
                    name="depots"
                    required
                  >
                    <Select
                      value={form.depots[0]?.key ?? ''}
                      disabled={readOnly}
                      onValueChange={(key) => {
                        const picked = depotOriginOptions.find((d) => d.key === key);
                        // Beim Erstellen automatisch auch als bewerbendes Depot
                        // vorausgewählt (15.09.2026) — rein additiv, siehe
                        // mergeDepotsByKey().
                        if (mode === 'create' && picked) {
                          setForm((f) => ({
                            ...f,
                            depots: [picked],
                            promotingDepots: mergeDepotsByKey(f.promotingDepots, [picked]),
                          }));
                        } else {
                          set('depots', picked ? [picked] : []);
                        }
                      }}
                    >
                      <SelectTrigger className="w-1/2">
                        <SelectValue placeholder={t('form.fields.depotPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {depotOriginOptions.map((d) => (
                          <SelectItem key={d.key} value={d.key}>
                            {formatDepot(d)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label={t('form.fields.vaType')} error={errors.vaType} name="vaType" required>
                    <Select value={form.vaType} disabled={readOnly} onValueChange={(v) => set('vaType', v)}>
                      <SelectTrigger className="w-1/2"><SelectValue placeholder={t('form.fields.vaTypePlaceholder')} /></SelectTrigger>
                      <SelectContent>
                        {/* Retired types are hidden, except when this event already
                            uses one — dropping it would silently blank the field.
                            Central-only ("hostedByCentral") types are hidden from
                            depot-scoped roles the same way (24.09.2026) — a partner
                            never picks one, though an existing event already on one
                            still resolves its name. */}
                        {eventTypes
                          .filter((et) => et.published !== false || et.id === form.vaType)
                          .filter((et) => !isPartner || et.hostedByCentral !== true || et.id === form.vaType)
                          .map((et) => (
                            <SelectItem key={et.id} value={et.id}>{et.name}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label={t('form.fields.title')} error={errors.title} name="title" required>
                    <Input
                      value={form.title}
                      disabled={readOnly}
                      onChange={(e) => set('title', e.target.value)}
                    />
                  </Field>

                  {/* Teaser — temporär ausgeblendet (Nutzervorgabe, 18.09.2026),
                      kommt später wieder, bewusst nicht gelöscht. Bis dahin
                      bleibt die audience-Bedingung stehen, sie greift wieder,
                      sobald das Feld reaktiviert wird. */}
                  {!TEASER_TEMPORARILY_HIDDEN && form.audience !== EventAudience.Closed && (
                    <Field label={t('form.fields.teaser')}>
                      <Input
                        value={form.teaser ?? ''}
                        disabled={readOnly}
                        placeholder={t('form.fields.teaserPlaceholder')}
                        onChange={(e) => set('teaser', e.target.value)}
                      />
                    </Field>
                  )}

                  {/* Sprechende URL (DCP-2228 follow-up, 18.09.2026) — nur Admin
                      darf sie ändern, alle anderen sehen die Live-Vorschau nur
                      lesend. Geschlossene Gesellschaften werden nie im
                      Storefront veröffentlicht (siehe publishedFor() im
                      Mapper), daher hier ebenfalls ausgeblendet wie der Teaser. */}
                  {form.audience !== EventAudience.Closed && (
                    <Field
                      label={t('form.fields.slug')}
                      error={errors.slug || (slugConflict ? t('form.errors.slugTaken') : undefined)}
                      name="slug"
                    >
                      <Input
                        value={form.slug ?? ''}
                        disabled={readOnly || !isAdmin}
                        onChange={(e) => {
                          setSlugTouched(true);
                          set('slug', e.target.value);
                        }}
                      />
                    </Field>
                  )}

                  <Field label={t('form.fields.audience')} error={errors.audience} name="audience" required>
                    <Select
                      value={form.audience}
                      disabled={readOnly}
                      onValueChange={(v) => {
                        const next = v as EventAudience;
                        // Geschlossene Gesellschaft: reduzierte Ansicht (15.09.2026) —
                        // Zahlung wird ausgeblendet, also muss costEnabled hier auf
                        // false gehen (sonst blockiert die Server-Pflichtprüfung für
                        // priceAmount ein Feld, das der Nutzer nicht mehr sieht), und
                        // "Online" fällt aus den Verkaufskanälen heraus (nur Depot).
                        if (next === EventAudience.Closed) {
                          setForm((f) => ({
                            ...f,
                            audience: next,
                            costEnabled: false,
                            salesChannels: (f.salesChannels ?? []).filter((id) => id !== 'commerce-engine'),
                          }));
                        } else {
                          set('audience', next);
                        }
                      }}
                    >
                      <SelectTrigger className="w-1/2"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.values(EventAudience).map((v) => (
                          <SelectItem key={v} value={v}>{t(`audience.${v}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  {/* Dates on one row, times below with the business-hours switch beside
                      them — the switch disables the time inputs, so keeping them
                      adjacent makes that relationship visible. */}
                  <div className="space-y-3">
                    <Field
                      label={t('form.fields.dateRange')}
                      error={errors.startAt || errors.endAt}
                      name={errors.endAt ? 'endAt' : 'startAt'}
                      required
                    >
                      <div className="flex items-center gap-2">
                        <Input
                          type="date"
                          className="w-[170px]"
                          disabled={readOnly}
                          value={start.date}
                          onChange={(e) => {
                            const date = e.target.value;
                            setForm((f) => {
                              const next = { ...f, startAt: combineDateTime(date, start.time) };
                              // Bis-Datum mitziehen, solange es noch leer ist — eintägige
                              // Veranstaltungen sind der Normalfall, ein zweiter Klick nur
                              // dafür wäre unnötig.
                              if (date && !end.date) {
                                next.endAt = combineDateTime(date, end.time);
                              }
                              // Anmelde-/Zahlungsfrist automatisch vorschlagen (25.09.2026,
                              // Nutzervorgabe) — zusätzlich zum Switch-Auto-Fill oben, weil
                              // costEnabled schon per Default an ist (der Switch dort also
                              // beim Anlegen nie umgelegt wird) und weil das Datum auch VOR
                              // dem Umlegen des jeweiligen Switches eingegeben werden kann.
                              if (date) {
                                if (f.registrationRequired && !f.registrationDeadline) {
                                  next.registrationDeadline = combineDateTime(
                                    deadlineDefault(date, f.plannedOnlineDate),
                                    ''
                                  );
                                }
                                if (f.costEnabled && !f.paymentDeadline) {
                                  next.paymentDeadline = combineDateTime(
                                    deadlineDefault(date, f.plannedOnlineDate),
                                    ''
                                  );
                                }
                              }
                              return next;
                            });
                          }}
                        />
                        <span className="text-muted-foreground">–</span>
                        <Input
                          type="date"
                          className="w-[170px]"
                          disabled={readOnly}
                          value={end.date}
                          onChange={(e) => set('endAt', combineDateTime(e.target.value, end.time))}
                        />
                        {/* "Serie erstellen" (24.09.2026) — title trägt den Hinweistext,
                            solange der Button inaktiv ist; kein Tooltip-Primitiv im Projekt.
                            Hellgrün wenn aktiv, hellgrau (auch Rahmen) wenn inaktiv
                            (Nutzervorgabe, 25.09.2026 nachgezogen — vorher fehlte der
                            Rahmen/Hintergrund im inaktiven Zustand trotz Kommentar). */}
                        <Button
                          type="button"
                          variant="outline"
                          className={
                            'ml-2 gap-2 ' +
                            (seriesEnabled
                              ? 'border-green-50 bg-green-50 text-green-700 hover:border-green-100 hover:bg-green-100 hover:text-green-800'
                              : 'border-gray-100 bg-gray-100 text-muted-foreground hover:bg-gray-100')
                          }
                          disabled={!seriesEnabled}
                          title={!seriesEnabled ? t('form.series.saveFirstHint') : undefined}
                          onClick={() => setSeriesDialogOpen(true)}
                        >
                          {t('form.series.createButton')}
                        </Button>
                      </div>
                    </Field>

                    <Field
                      label={t('form.fields.timeRange')}
                      error={errors.time}
                      name="time"
                      required={!form.businessHours}
                    >
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        <div className="flex items-center gap-2">
                          <Input
                            type="text"
                            placeholder="--:--"
                            className="w-[72px]"
                            disabled={readOnly || form.businessHours}
                            value={startTimeText}
                            onChange={(e) => {
                              const text = formatTimeInput(e.target.value);
                              setStartTimeText(text);
                              // Nur bei vollständig gültiger Uhrzeit sofort ins
                              // Formular übernehmen (siehe Kommentar bei den
                              // startTimeText/endTimeText-States oben) — validate()
                              // meldet eine unvollständige/ungültige Eingabe beim
                              // Speichern, nicht bei jedem Tastendruck.
                              if (!TIME_PATTERN.test(text)) return;
                              // Datum defaulten, falls noch leer (Bug fix 21.09.2026):
                              // combineDateTime() liefert ohne Datum '' zurück — eine
                              // Uhrzeit-Eingabe VOR der Datumswahl verschwand dadurch
                              // stillschweigend wieder. Wer danach noch ein echtes
                              // Datum setzt, überschreibt diesen Platzhalter über das
                              // Datumsfeld oben — dessen onChange übernimmt start.time
                              // unverändert.
                              set('startAt', combineDateTime(start.date || todayDateOnly(), text));
                              // Bis-Uhrzeit mitziehen, solange sie noch leer ist —
                              // analog zum Bis-Datum oben.
                              if (!endTimeText) {
                                setEndTimeText(text);
                                set('endAt', combineDateTime(end.date || todayDateOnly(), text));
                              }
                            }}
                          />
                          <span className="text-muted-foreground">–</span>
                          <Input
                            type="text"
                            placeholder="--:--"
                            className="w-[72px]"
                            disabled={readOnly || form.businessHours}
                            value={endTimeText}
                            onChange={(e) => {
                              const text = formatTimeInput(e.target.value);
                              setEndTimeText(text);
                              if (!TIME_PATTERN.test(text)) return;
                              // Gleicher Grund wie beim Start-Feld oben.
                              set('endAt', combineDateTime(end.date || todayDateOnly(), text));
                            }}
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={form.businessHours}
                            disabled={readOnly}
                            onCheckedChange={(v) => {
                              set('businessHours', v);
                              if (v) {
                                // Geschäftszeiten-Reset (24.09.2026): eine bereits
                                // eingegebene Uhrzeit ergibt bei aktiven
                                // Geschäftszeiten keinen Sinn mehr und wird geleert,
                                // nicht nur ausgeblendet/deaktiviert.
                                setStartTimeText('');
                                setEndTimeText('');
                                set('startAt', combineDateTime(start.date || todayDateOnly(), ''));
                                set('endAt', combineDateTime(end.date || todayDateOnly(), ''));
                              }
                            }}
                          />
                          <label className="text-sm font-medium">
                            {t('form.fields.businessHours')}
                          </label>
                        </div>
                      </div>
                    </Field>
                  </div>

                  {/* Veranstaltungsort — Überschrift entfernt (Nutzervorgabe,
                      18.09.2026), das Feld-Label "locationType" heißt jetzt
                      selbst "Veranstaltungsort". */}
                  <div className="space-y-3">
                    <Field label={t('form.fields.locationType')}>
                      <Select value={form.locationType} disabled={readOnly} onValueChange={(v) => set('locationType', v as LocationType)}>
                        <SelectTrigger className="w-1/2"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="depot">{t('location.depot')}</SelectItem>
                          <SelectItem value="external">{t('location.external')}</SelectItem>
                          <SelectItem value="online">{t('location.online')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>

                    {form.locationType === 'external' && (
                      <div className="space-y-4 rounded-md border p-3 bg-muted/30">
                        <Field label={t('form.fields.externalName')} error={errors.externalName} name="externalName" required>
                          <Input disabled={readOnly} value={form.externalName ?? ''} onChange={(e) => set('externalName', e.target.value)} />
                        </Field>
                        <div className="grid grid-cols-4 gap-4">
                          <div className="col-span-3">
                            <Field label={t('form.fields.externalStreet')} error={errors.externalStreet} name="externalStreet" required>
                              <Input disabled={readOnly} value={form.externalStreet ?? ''} onChange={(e) => set('externalStreet', e.target.value)} />
                            </Field>
                          </div>
                          <Field label={t('form.fields.externalHouseNo')} error={errors.externalHouseNo} name="externalHouseNo" required>
                            <Input disabled={readOnly} value={form.externalHouseNo ?? ''} onChange={(e) => set('externalHouseNo', e.target.value)} />
                          </Field>
                        </div>
                        <div className="grid grid-cols-4 gap-4">
                          <Field label={t('form.fields.externalZip')} error={errors.externalZip} name="externalZip" required>
                            <Input disabled={readOnly} value={form.externalZip ?? ''} onChange={(e) => set('externalZip', e.target.value)} />
                          </Field>
                          <div className="col-span-2">
                            <Field label={t('form.fields.externalCity')} error={errors.externalCity} name="externalCity" required>
                              <Input disabled={readOnly} value={form.externalCity ?? ''} onChange={(e) => set('externalCity', e.target.value)} />
                            </Field>
                          </div>
                          <Field label={t('form.fields.externalCountry')} error={errors.externalCountry} name="externalCountry" required>
                            <Input disabled={readOnly} value={form.externalCountry ?? ''} onChange={(e) => set('externalCountry', e.target.value)} />
                          </Field>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <Divider />

                {/* Bewerbung + Mailings — bei geschlossener Gesellschaft komplett
                    ausgeblendet (22.09.2026-Follow-up): eine private Veranstaltung
                    wird nicht an andere Depots beworben und verschickt auch keine
                    Mailings. "Geplante Onlinestellung" hatte diese Regel schon
                    (15.09.2026), "Bewerbende Depots" und der ganze Mailings-Block
                    fehlten dabei — jetzt eine gemeinsame Bedingung für den
                    kompletten Abschnitt statt einzeln je Feld. */}
                {form.audience !== EventAudience.Closed && (
                  <>
                    {/* Bewerbung */}
                    <div className="space-y-3">
                      <SectionTitle>{t('form.sections.promotion')}</SectionTitle>

                      {/* Promoting depots — autosuggest input (fast entry for many depots) */}
                      <Field
                        label={t('form.fields.promotingDepots')}
                        error={errors.promotingDepots}
                        name="promotingDepots"
                        required
                        hint={t('form.fields.promotingDepotsHint')}
                      >
                        <DepotAutosuggest
                          selected={withNames(form.promotingDepots)}
                          options={allDepots}
                          disabled={readOnly}
                          placeholder={t('form.fields.promotingPlaceholder')}
                          emptyLabel={t('form.fields.promotingEmpty')}
                          noMatchLabel={t('form.fields.promotingNoMatch')}
                          // Sofortige Fehleranzeige beim Leeren (25.09.2026,
                          // Nutzervorgabe): das Entfernen eines Chips verlässt
                          // das Feld nicht (kein Blur), also muss der Fehler
                          // hier selbst gesetzt/gelöscht werden — gleiches
                          // Merge-Muster wie handleFieldBlur.
                          onChange={(depots) => {
                            set('promotingDepots', depots);
                            const message =
                              depots.length === 0
                                ? t('form.errors.promotingDepotRequired')
                                : undefined;
                            setErrors((prev) => {
                              if (message) {
                                if (prev.promotingDepots === message) return prev;
                                return { ...prev, promotingDepots: message };
                              }
                              if (!('promotingDepots' in prev)) return prev;
                              const next = { ...prev };
                              delete next.promotingDepots;
                              return next;
                            });
                          }}
                        />
                      </Field>

                      <Field
                        label={t('form.fields.plannedOnlineDate')}
                        error={errors.plannedOnlineDate}
                        name="plannedOnlineDate"
                        required
                      >
                        <Input
                          type="date"
                          className="w-[170px]"
                          // Auch für Depot-Partner änderbar (03.11.2026) — aber nur auf einen
                          // Montag; die Prüfung dazu steht in validate(). Zentrale und Admin
                          // sind unbeschränkt.
                          disabled={readOnly}
                          value={dateOnlyPart(form.plannedOnlineDate)}
                          onChange={(e) => {
                            const value = combineDateTime(e.target.value, '');
                            set('plannedOnlineDate', value);
                            checkOnlineDateOnPick(value);
                          }}
                        />
                      </Field>
                    </div>

                    {/* Mailings — read-only, derived from startAt. Also shown while
                        creating: the panel computes against the form field, so it fills in
                        as soon as a date is picked. */}
                    <div className="space-y-3">
                      <SectionTitle>{t('form.sections.mailings')}</SectionTitle>
                      <MailingPanel
                        mailings={mailings}
                        startAt={form.startAt}
                        // hasExtraPeriod lives on the event TYPE, not the form itself —
                        // looked up here rather than passed down, since only this call
                        // site cares about it.
                        hasExtraPeriod={
                          eventTypes.find((et) => et.id === form.vaType)?.hasExtraPeriod === true
                        }
                        isLoading={mailingsLoading}
                        t={t}
                      />
                    </div>
                  </>
                )}
              </>
            )}

            {activeTab === 2 && (
              <>
                {/* Beschreibungstext + Vorlage (Kategorie → Vorlage) */}
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <Field label={t('form.fields.presetCategory')}>
                      <Select
                        value={presetCategory || 'none'}
                        disabled={readOnly}
                        onValueChange={(v) => {
                          // Kategoriewechsel verwirft die Vorlagenwahl: die alte Vorlage
                          // gehört zur alten Kategorie und stünde sonst als Auswahl da,
                          // die in der Liste nicht mehr vorkommt.
                          setPresetCategory(v === 'none' ? '' : v);
                          set('presetTextId', undefined);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('form.fields.presetCategoryPlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t('form.fields.presetTextNone')}</SelectItem>
                          {eventTextCategories.map((category) => (
                            <SelectItem key={category} value={category}>
                              {category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field label={t('form.fields.presetText')}>
                      <Select
                        value={form.presetTextId ?? 'none'}
                        // Ohne Kategorie gesperrt statt alle Vorlagen zeigen — sonst wäre
                        // die Kategoriewahl ohne Wirkung.
                        disabled={readOnly || !presetCategory}
                        onValueChange={(v) => {
                          if (v === 'none') {
                            set('presetTextId', undefined);
                            return;
                          }
                          const vorlage = presetOptions.find((p) => p.id === v);
                          if (!vorlage) return;

                          // Gefüllten Text nicht ohne Rückfrage überschreiben. Vorher war
                          // das der Fall — wer nach dem Schreiben aus Neugier eine Vorlage
                          // anklickte, verlor seinen Text.
                          if (!isBlankMarkdown(form.description)) {
                            setPendingPreset(vorlage);
                            return;
                          }

                          set('presetTextId', v);
                          set('description', vorlage.text);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t(
                              presetCategory
                                ? 'form.fields.presetTextPlaceholder'
                                : 'form.fields.presetTextPlaceholderNoCategory'
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t('form.fields.presetTextNone')}</SelectItem>
                          {presetOptions.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <Field
                    label={t('form.fields.description')}
                    error={errors.description}
                    name="description"
                    required
                    hint={t('form.fields.descriptionHint')}
                  >
                    <RichTextEditor
                      value={form.description ?? ''}
                      disabled={readOnly}
                      onChange={(html) => set('description', html)}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('form.fields.descriptionCounter', { count: plainTextLength(form.description) })}
                    </p>
                  </Field>
                </div>
              </>
            )}

            {activeTab === 3 && (
              <>
                {/* Verfügbare Plätze + SalesChannels (Kontingent) */}
                <div className="space-y-3">
                  <SectionTitle>{t('form.sections.seats')}</SectionTitle>
                  <div className={isCentral ? 'grid grid-cols-2 gap-4' : ''}>
                    <Field
                      label={t(
                        form.audience === EventAudience.Closed ? 'form.fields.qtyClosed' : 'form.fields.qty'
                      )}
                      error={errors.qty}
                      name="qty"
                      required={form.registrationRequired && form.costEnabled}
                    >
                      <Input
                        type="number"
                        min={committedSeats(event)}
                        className="w-[200px]"
                        disabled={!qtyEditable}
                        value={form.qty ?? ''}
                        onChange={(e) =>
                          set('qty', e.target.value === '' ? undefined : Number(e.target.value))
                        }
                      />
                      {event?.available != null && (
                        <p className="text-xs text-muted-foreground">
                          {t('form.fields.availableHint', {
                            available: event.available,
                            qty: event.qty ?? '?',
                            sold: event.sold ?? 0,
                          })}
                        </p>
                      )}
                      {/* Names the floor before the user hits it — reserviert +
                          verkauft, nicht nur verkauft (12.10.2026, Nutzervorgabe,
                          siehe committedSeats()). Der Server prüft dieselbe
                          Grenze gegen die ticket-api. */}
                      {committedSeats(event) > 0 && (
                        <p className="text-xs text-amber-700">
                          {t('form.fields.qtyMinHint', { sold: committedSeats(event) })}
                        </p>
                      )}
                    </Field>
                    {/* Nur Admin/Zentrale — für alle anderen ausgeblendet, im
                        Hintergrund bleiben default beide Kanäle aktiv (15.09.2026). */}
                    {isCentral && (
                      <Field
                        label={t('form.fields.salesChannels')}
                        error={errors.salesChannels}
                        name="salesChannels"
                        required={(form.qty ?? 0) > 0}
                      >
                        <div className="space-y-2 rounded-md border p-3">
                          {salesChannels.map((sc) => {
                            const checked = (form.salesChannels ?? []).includes(sc.id);
                            // Geschlossene Gesellschaft: nur Depot, "Online"
                            // (commerce-engine) ist deaktiviert (15.09.2026) — der
                            // Wert selbst wurde beim Audience-Wechsel schon bereinigt.
                            const onlineDisabledWhenClosed =
                              form.audience === EventAudience.Closed && sc.id !== 'depot-pos';
                            return (
                              <label key={sc.id} className="flex items-center gap-2 text-sm">
                                <Checkbox
                                  checked={checked}
                                  disabled={readOnly || onlineDisabledWhenClosed}
                                  onCheckedChange={(v) => {
                                    const cur = form.salesChannels ?? [];
                                    set(
                                      'salesChannels',
                                      v === true ? [...cur, sc.id] : cur.filter((id) => id !== sc.id)
                                    );
                                  }}
                                />
                                {sc.name}
                              </label>
                            );
                          })}
                        </div>
                      </Field>
                    )}
                  </div>
                  {/* Anlass + Auftraggeber — nur bei geschlossener Gesellschaft
                      (15.09.2026), direkt unter den Verkaufskanälen. */}
                  {form.audience === EventAudience.Closed && (
                    <div className="grid grid-cols-2 gap-4">
                      <Field label={t('form.fields.eventReason')}>
                        <Select
                          value={form.eventReason ?? ''}
                          disabled={readOnly}
                          onValueChange={(v) => set('eventReason', v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('form.fields.eventReasonPlaceholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            {EVENT_REASON_OPTIONS.map((key) => (
                              <SelectItem key={key} value={key}>{t(`eventReason.${key}`)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label={t('form.fields.eventClient')}>
                        <Input
                          value={form.eventClient ?? ''}
                          disabled={readOnly}
                          onChange={(e) => set('eventClient', e.target.value)}
                        />
                      </Field>
                    </div>
                  )}
                </div>

                {/* Finanzielle Unterstützung — für Depot-Partner wieder komplett
                    ausgeblendet (Nutzervorgabe, 18.09.2026, macht das
                    16.09.2026-Follow-up rückgängig: dort wurde die Sektion für
                    jede Rolle sichtbar gemacht). Glas-/Gedeckverkauf ist von
                    hier in die Zahlung-Sektion umgezogen (siehe dort) und
                    bleibt deshalb für jede Rolle sichtbar. */}
                {!isPartner && (
                  <>
                    <Divider />
                    <div className="space-y-3">
                      <SectionTitle>{t('form.sections.financialSupport')}</SectionTitle>
                      <div className="flex items-center justify-between text-sm">
                        <span>{t('form.fields.financialSupportCalculated')}</span>
                        <span className="text-right">
                          <span className="font-medium">
                            {formatCurrency(matchedFinancialSupport?.flatRateAmount ?? 0, 'EUR')}
                            {` (${t('refund.wines.priceNet')})`}
                            {matchedFinancialSupport && ` — ${matchedFinancialSupport.name}`}
                          </span>
                          {/* Die Pauschale selbst ist netto gepflegt (siehe
                              flatRateNetCentsFor) — die MwSt dafür kommt vom
                              Event selbst (Zahlung/taxCategory unten), nicht aus
                              der Pflege "Finanzielle Unterstützung". Reine
                              Vorschau des Brutto-Betrags, den die Abrechnung
                              daraus berechnen wird (21.09.2026-Follow-up). */}
                          {matchedFinancialSupport && (
                            <div className="text-xs text-muted-foreground">
                              {formatCurrency(
                                toPriceFromNet(
                                  Math.round(matchedFinancialSupport.flatRateAmount * 100),
                                  'EUR',
                                  form.taxCategory ?? DEFAULT_TAX_CATEGORY
                                ).brutto.centAmount / 100,
                                'EUR'
                              )}{' '}
                              ({t('refund.wines.priceGross')} · {t(`taxCategory.${form.taxCategory ?? DEFAULT_TAX_CATEGORY}`)})
                            </div>
                          )}
                        </span>
                      </div>
                      {!matchedFinancialSupport && (
                        <p className="text-xs text-muted-foreground">
                          {t('form.fields.financialSupportNoMatch')}
                        </p>
                      )}
                      {isCentral && (
                        <label className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={form.financialSupportApproved ?? false}
                            disabled={readOnly}
                            onCheckedChange={(v) => set('financialSupportApproved', v === true)}
                          />
                          {t('form.fields.financialSupportApproved')}
                        </label>
                      )}
                    </div>
                  </>
                )}


                {/* Anmeldung + Zahlung + Bankverbindung + Stornobedingungen — bei
                    geschlossener Gesellschaft komplett ausgeblendet (15.09.2026),
                    inkl. ihrer Divider. */}
                {form.audience !== EventAudience.Closed && (
                  <>
                    <Divider />

                    {/* Anmeldung */}
                    <div className="space-y-3">
                      <SectionTitle>{t('form.sections.registration')}</SectionTitle>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={form.registrationRequired}
                          disabled={readOnly}
                          onCheckedChange={(v) => {
                            // Anmeldefrist mitziehen, sobald "mit Anmeldung" aktiviert
                            // wird — nur wenn noch leer, wie beim endAt-Auto-Fill
                            // (Nutzervorgabe, 18.09.2026): 3 Tage vor Datum von, nie vor
                            // der Onlinestellung (25.09.2026, deadlineDefault()).
                            if (v && !form.registrationDeadline && form.startAt) {
                              // splitDateTime() statt dateOnlyPart(): startAt trägt bei
                              // einer echten Uhrzeit (anders als registrationDeadline/
                              // plannedOnlineDate) keine UTC-Mitternacht — ein simples
                              // slice(0,10) auf den ISO-String zeigt bei früher Ortszeit
                              // (z. B. 00:xx/01:xx CEST) den UTC-Vortag.
                              const startDay = splitDateTime(form.startAt).date;
                              const deadline = deadlineDefault(startDay, form.plannedOnlineDate);
                              setForm((f) => ({
                                ...f,
                                registrationRequired: v,
                                registrationDeadline: combineDateTime(deadline, ''),
                              }));
                            } else {
                              set('registrationRequired', v);
                            }
                          }}
                        />
                        <label className="text-sm font-medium">
                          {form.registrationRequired
                            ? t('form.fields.registrationRequiredOn')
                            : t('form.fields.registrationRequiredOff')}
                        </label>
                      </div>
                      {form.registrationRequired && (
                        <div className="grid grid-cols-2 gap-4">
                          <Field
                            label={t('form.fields.registrationDeadline')}
                            error={errors.registrationDeadline}
                            name="registrationDeadline"
                            required
                          >
                            <Input
                              type="date"
                              disabled={readOnly}
                              value={dateOnlyPart(form.registrationDeadline)}
                              onChange={(e) => set('registrationDeadline', combineDateTime(e.target.value, ''))}
                            />
                          </Field>
                        </div>
                      )}
                    </div>

                    <Divider />

                    {/* Zahlung (Kostenbeitrag + Zahlungsart) */}
                    <div className="space-y-3">
                      <SectionTitle>{t('form.sections.payment')}</SectionTitle>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={form.costEnabled}
                          disabled={readOnly}
                          onCheckedChange={(v) => {
                            if (!v) {
                              setForm((f) => ({ ...f, costEnabled: false, taxCategory: DEFAULT_TAX_CATEGORY }));
                              return;
                            }

                            // Zahlungsfrist mitziehen, sobald "Kostenpflichtig" aktiviert wird —
                            // nur wenn noch leer, dasselbe Muster wie beim registrationRequired-
                            // Switch oben (Nutzervorgabe, 22.09.2026): 3 Tage vor Datum von, nie
                            // vor der Onlinestellung (25.09.2026, deadlineDefault()).
                            if (!form.paymentDeadline && form.startAt) {
                              // splitDateTime() statt dateOnlyPart(): startAt trägt bei einer
                              // echten Uhrzeit (anders als paymentDeadline/plannedOnlineDate)
                              // keine UTC-Mitternacht — ein simples slice(0,10) auf den
                              // ISO-String zeigt bei früher Ortszeit (z. B. 00:xx/01:xx CEST)
                              // den UTC-Vortag.
                              const startDay = splitDateTime(form.startAt).date;
                              const deadline = deadlineDefault(startDay, form.plannedOnlineDate);
                              setForm((f) => ({
                                ...f,
                                costEnabled: true,
                                paymentDeadline: combineDateTime(deadline, ''),
                              }));
                            } else {
                              set('costEnabled', true);
                            }
                          }}
                        />
                        <label className="text-sm font-medium">
                          {form.costEnabled
                            ? t('form.fields.costEnabledOn')
                            : t('form.fields.costEnabledOff')}
                        </label>
                      </div>
                      {form.costEnabled && (
                        <>
                          <div className="grid grid-cols-4 gap-4">
                            <Field label={t('form.fields.priceAmount')} error={errors.priceAmount} name="priceAmount" required>
                              <Input
                                type="number"
                                min={0}
                                step="0.01"
                                disabled={readOnly}
                                value={form.priceAmount ?? ''}
                                onChange={(e) =>
                                  set('priceAmount', e.target.value === '' ? undefined : Number(e.target.value))
                                }
                              />
                            </Field>
                            <Field label={t('form.fields.priceUnit')}>
                              <Select
                                value={form.priceUnit}
                                disabled={readOnly}
                                onValueChange={(v) => set('priceUnit', v as PriceUnit)}
                              >
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="person">{t('priceUnit.person')}</SelectItem>
                                  <SelectItem value="package">{t('priceUnit.package')}</SelectItem>
                                </SelectContent>
                              </Select>
                            </Field>
                            <Field label={t('form.fields.taxCategory')}>
                              <Select
                                value={form.taxCategory ?? DEFAULT_TAX_CATEGORY}
                                disabled={readOnly}
                                onValueChange={(v) => set('taxCategory', v as TaxCategoryKey)}
                              >
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {TAX_CATEGORY_OPTIONS.map((key) => (
                                    <SelectItem key={key} value={key}>
                                      {t(`taxCategory.${key}`)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </Field>
                          </div>
                          <div className="grid grid-cols-4 gap-4">
                            <Field label={t('form.fields.advantagePriceAmount')} labelClassName="whitespace-nowrap">
                              <Input
                                type="number"
                                min={0}
                                step="0.01"
                                disabled={readOnly}
                                value={form.advantagePriceAmount ?? ''}
                                onChange={(e) =>
                                  set(
                                    'advantagePriceAmount',
                                    // null statt undefined: undefined fiele beim Senden aus dem JSON,
                                    // und der Server behielte den alten Preis.
                                    e.target.value === '' ? null : Number(e.target.value)
                                  )
                                }
                              />
                            </Field>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <Field label={t('form.fields.paymentMethod')}>
                              <Select
                                value={form.paymentMethod ?? 'null'}
                                disabled={readOnly}
                                onValueChange={(v) => {
                                  const method = v === 'null' ? null : (v as PaymentMethod);
                                  // Automatik statt manueller Vorlagenwahl (15.09.2026) —
                                  // die Zahlungstext-Vorlage kommt jetzt über ihre
                                  // Zahlungsart-Zuordnung, kein Dropdown mehr dafür.
                                  const vorlage = paymentNoteTemplates.find((t) => t.paymentMethod === method);
                                  if (vorlage && !isBlankMarkdown(form.paymentNote)) {
                                    set('paymentMethod', method);
                                    setPendingPaymentNoteTemplate(vorlage);
                                    return;
                                  }
                                  // Wechsel auf Individuellen Text leert den Zahlungstext
                                  // (24.09.2026, Nutzervorgabe) — anders als ein Wechsel
                                  // zwischen den Vorlagen-Zahlarten oben, wo ein bereits
                                  // gefüllter Text vor dem Überschreiben erst bestätigt
                                  // werden muss: hier gibt es keine Vorlage, die den alten
                                  // Text sinnvoll ersetzen könnte.
                                  setForm((f) => ({
                                    ...f,
                                    paymentMethod: method,
                                    ...(vorlage
                                      ? { paymentNoteTemplateId: vorlage.id, paymentNote: vorlage.text }
                                      : method === null
                                        ? { paymentNoteTemplateId: undefined, paymentNote: '' }
                                        : {}),
                                  }));
                                }}
                              >
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="depot_or_transfer">{t('paymentMethod.depot_or_transfer')}</SelectItem>
                                  <SelectItem value="depot">{t('paymentMethod.depot')}</SelectItem>
                                  <SelectItem value="transfer">{t('paymentMethod.transfer')}</SelectItem>
                                  <SelectItem value="null">{t('paymentMethod.null')}</SelectItem>
                                </SelectContent>
                              </Select>
                            </Field>
                            <Field
                              label={t('form.fields.paymentDeadline')}
                              error={errors.paymentDeadline}
                              name="paymentDeadline"
                              required
                            >
                              <Input
                                type="date"
                                disabled={readOnly}
                                value={dateOnlyPart(form.paymentDeadline)}
                                onChange={(e) => set('paymentDeadline', combineDateTime(e.target.value, ''))}
                              />
                            </Field>
                            <div className="col-span-2">
                              <Field label={t('form.fields.paymentNote')} error={errors.paymentNote} name="paymentNote" required>
                                <RichTextEditor
                                  disabled={readOnly}
                                  value={form.paymentNote ?? ''}
                                  onChange={(markdown) => set('paymentNote', markdown)}
                                />
                              </Field>
                            </div>
                          </div>
                        </>
                      )}
                      {/* Glas- oder Gedeckverkauf: immer sichtbar, unabhängig von
                          costEnabled (Nutzervorgabe, 18.09.2026) — Pflichtfeld.
                          Überschrift „Verkauf" + Label/Dropdown in einer Zeile,
                          Dropdown rechtsbündig (29.09.2026, Nutzervorgabe) —
                          der erklärende Hinweistext darunter entfällt dabei. */}
                      <div className="space-y-2">
                        <SectionTitle>{t('form.sections.sale')}</SectionTitle>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <span className="text-sm text-muted-foreground">
                            {t('form.fields.glassOrTableSale')}
                            <span className="text-red-600"> *</span>
                          </span>
                          <Select
                            value={form.glassOrTableSale === undefined ? 'null' : String(form.glassOrTableSale)}
                            disabled={readOnly}
                            onValueChange={(v) => set('glassOrTableSale', v === 'null' ? undefined : v === 'true')}
                          >
                            <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="null">{t('glassOrTableSale.null')}</SelectItem>
                              <SelectItem value="true">{t('glassOrTableSale.true')}</SelectItem>
                              <SelectItem value="false">{t('glassOrTableSale.false')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {errors.glassOrTableSale && (
                          <p className="text-xs text-red-600">{errors.glassOrTableSale}</p>
                        )}
                      </div>
                    </div>

                    <Divider />

                    {/* Ihre Bankverbindung */}
                    <div className="space-y-3">
                      <SectionTitle>{t('form.sections.bankDetails')}</SectionTitle>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={form.publishAccountData ?? true}
                          disabled={readOnly}
                          onCheckedChange={(v) => set('publishAccountData', v)}
                        />
                        <label className="text-sm font-medium">
                          {(form.publishAccountData ?? true)
                            ? t('form.fields.publishAccountDataOn')
                            : t('form.fields.publishAccountDataOff')}
                        </label>
                      </div>
                      {(form.publishAccountData ?? true) && (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <Field
                              label={t('form.fields.paymentAccountHolder')}
                              error={errors.paymentAccountHolder}
                              name="paymentAccountHolder"
                              required
                            >
                              <Input
                                disabled={readOnly}
                                value={form.paymentAccountHolder ?? ''}
                                onChange={(e) => set('paymentAccountHolder', e.target.value)}
                              />
                            </Field>
                          </div>
                          <Field label={t('form.fields.paymentIban')} error={errors.paymentIban} name="paymentIban" required>
                            <Input
                              disabled={readOnly}
                              value={form.paymentIban ?? ''}
                              placeholder="DE89 3704 0044 0532 0130 00"
                              onChange={(e) => set('paymentIban', e.target.value)}
                            />
                          </Field>
                          <Field label={t('form.fields.paymentBic')} error={errors.paymentBic} name="paymentBic" required>
                            <Input
                              disabled={readOnly}
                              value={form.paymentBic ?? ''}
                              placeholder="COBADEFFXXX"
                              onChange={(e) => set('paymentBic', e.target.value)}
                            />
                          </Field>
                          <div className="col-span-2">
                            <Field
                              label={t('form.fields.paymentReference')}
                              error={errors.paymentReference}
                              name="paymentReference"
                              required
                            >
                              <Input
                                disabled={readOnly}
                                value={form.paymentReference ?? ''}
                                onChange={(e) => set('paymentReference', e.target.value)}
                              />
                            </Field>
                          </div>
                        </div>
                      )}
                    </div>

                    <Divider />

                    {/* Stornobedingungen (US-304/305): Aufbau wie bei paymentNote
                        (Auswahl → RichTextEditor) — die Vorlage füllt
                        `cancellationNote` nur vor, der Wortlaut bleibt danach
                        frei editierbar. */}
                    <div className="space-y-3">
                      <SectionTitle>{t('form.sections.cancellationTerms')}</SectionTitle>
                      <Field label={t('form.fields.cancellationTerms')}>
                        <Select
                          value={form.cancellationTermsId ?? 'none'}
                          disabled={readOnly}
                          onValueChange={(v) => {
                            if (v === 'none') {
                              set('cancellationTermsId', undefined);
                              set('cancellationNote', undefined);
                              return;
                            }
                            const bedingung = cancellationTerms.find((c) => c.id === v);
                            if (!bedingung) return;

                            // Gefüllten Text nicht ohne Rückfrage überschreiben — dasselbe
                            // Muster wie bei der Vorlagenauswahl des Beschreibungstexts, seit
                            // der Wortlaut hier ebenfalls frei editierbar ist.
                            if (form.cancellationNote?.trim()) {
                              setPendingCancellationTerm(bedingung);
                              return;
                            }

                            // Kopiert wie die Vorlage des Beschreibungstexts (Entscheidung OF-7):
                            // eine spätere Änderung an der Bedingung erreicht diese VA nicht.
                            set('cancellationTermsId', v);
                            set('cancellationNote', bedingung.text);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('form.fields.cancellationTermsPlaceholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{t('form.fields.cancellationTermsNone')}</SelectItem>
                            {cancellationTerms.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>

                      <Field label={t('form.fields.cancellationTermsText')}>
                        <RichTextEditor
                          value={form.cancellationNote ?? ''}
                          disabled={readOnly}
                          onChange={(markdown) => set('cancellationNote', markdown)}
                          // Vorerst unformatiert (29.09.2026, Nutzervorgabe) — nur die
                          // Leiste ausgeblendet, der Editor bleibt voll nutzbar.
                          hideToolbar
                        />
                      </Field>
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* Unauffällig, nur für Admin (21.09.2026-Follow-up) — z. B. für
              Support-Rückfragen/Log-Abgleich, ohne im normalen Betrieb zu stören.
              identifier, nicht ctId (12.10.2026, Nutzervorgabe): identifier ist
              bei CT-gestützten Events der object.key (depot-event-mapper.ts) —
              das ist der Wert, den man in commercetools/den Logs als
              custom-object.key wiederfindet. ctId wäre die interne UUID,
              dieselbe, die in der ticket-api/DynamoDB als event_id steht. */}
          {role === Role.Admin && event && (
            <p className="flex items-center gap-1 pt-4 text-[10px] text-muted-foreground/50">
              Key: {event.identifier}
              <CopyKeyButton value={event.identifier} />
            </p>
          )}
          </div>

          <DialogFooter className="flex-shrink-0 border-t p-6 pt-4">
            <FooterActions
              mode={mode}
              status={event?.status}
              isCentral={isCentral}
              frozenForPartner={frozenForPartner}
              activeTab={activeTab}
              onCancel={() => onOpenChange(false)}
              onBack={activeTab > 1 ? () => goToTab((activeTab - 1) as 1 | 2) : undefined}
              onSave={handleSave}
              onSaveAndContinue={handleSaveAndContinue}
              onSubmit={handleSubmitForReview}
              onApprove={withSave(onApprove)}
              onReject={() => setConfirmReject(true)}
              onReopen={() => setConfirmReopenEvent(true)}
              // Altdaten aus dem six-cms-Import haben nie ein von admin-app
              // verwaltetes Kontingent (DCP-2228 follow-up, 18.09.2026) — für
              // sie darf keine Abrechnung entstehen.
              onOpenRefund={event?.eventsVersion === 'v2' ? onOpenRefund : undefined}
              t={t}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vorlage würde einen gefüllten Beschreibungstext überschreiben — vorher fragen.
          Vorher geschah das ohne Rückfrage: wer nach dem Schreiben aus Neugier eine
          Vorlage anklickte, verlor seinen Text. */}
      <ConfirmationDialog
        open={pendingPreset !== null}
        onOpenChange={(o) => !o && setPendingPreset(null)}
        title={t('form.presetReplace.title')}
        description={t('form.presetReplace.description')}
        confirmLabel={t('form.presetReplace.confirm')}
        cancelLabel={t('actions.cancel')}
        variant="destructive"
        onConfirm={() => {
          if (!pendingPreset) return;
          set('presetTextId', pendingPreset.id);
          set('description', pendingPreset.text);
          setPendingPreset(null);
        }}
      />

      {/* Dasselbe für die Stornobedingung, seit ihr Wortlaut ebenfalls frei editierbar ist. */}
      <ConfirmationDialog
        open={pendingCancellationTerm !== null}
        onOpenChange={(o) => !o && setPendingCancellationTerm(null)}
        title={t('form.cancellationTermsReplace.title')}
        description={t('form.cancellationTermsReplace.description')}
        confirmLabel={t('form.cancellationTermsReplace.confirm')}
        cancelLabel={t('actions.cancel')}
        variant="destructive"
        onConfirm={() => {
          if (!pendingCancellationTerm) return;
          set('cancellationTermsId', pendingCancellationTerm.id);
          set('cancellationNote', pendingCancellationTerm.text);
          setPendingCancellationTerm(null);
        }}
      />

      {/* Dasselbe für den Zahlungstext. */}
      <ConfirmationDialog
        open={pendingPaymentNoteTemplate !== null}
        onOpenChange={(o) => !o && setPendingPaymentNoteTemplate(null)}
        title={t('form.paymentNoteReplace.title')}
        description={t('form.paymentNoteReplace.description')}
        confirmLabel={t('form.paymentNoteReplace.confirm')}
        cancelLabel={t('actions.cancel')}
        variant="destructive"
        onConfirm={() => {
          if (!pendingPaymentNoteTemplate) return;
          set('paymentNoteTemplateId', pendingPaymentNoteTemplate.id);
          set('paymentNote', pendingPaymentNoteTemplate.text);
          setPendingPaymentNoteTemplate(null);
        }}
      />

      {/* Reject requires a reason (standalone dialog — ConfirmationDialog has no children slot) */}
      <Dialog open={confirmReject} onOpenChange={(o) => { if (!o) setConfirmReject(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('form.reject.title')}</DialogTitle>
            <DialogDescription>{t('form.reject.description')}</DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder={t('form.reject.reasonPlaceholder')}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          {confirmReject && !rejectReason.trim() && (
            <p className="text-xs text-red-600">{t('form.reject.reasonRequired')}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmReject(false)}>
              {t('actions.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={!rejectReason.trim()}
              onClick={() => {
                // Bewusst OHNE pendingSavePayload()/validate() (Nutzervorgabe,
                // 25.09.2026): Zurückweisen prüft keine Formularfelder und
                // speichert keine Formulardaten — nur Status+Kommentar.
                if (!rejectReason.trim()) return;
                onReject(rejectReason.trim());
                setConfirmReject(false);
                setRejectReason('');
              }}
            >
              {t('form.reject.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gegen Fehlklicks (22.09.2026-Follow-up): drei reine Ja/Nein-Rückfragen
          ohne Freitext, dieselbe Stelle wie oben, nur ohne den Reason-Umweg —
          withSave() greift wie gehabt erst beim tatsächlichen Bestätigen, nicht
          schon beim Öffnen der Rückfrage. */}
      <ConfirmationDialog
        open={confirmCancelEvent}
        onOpenChange={setConfirmCancelEvent}
        title={t('form.confirmCancelEvent.title')}
        description={t('form.confirmCancelEvent.description')}
        confirmLabel={t('form.confirmCancelEvent.confirm')}
        cancelLabel={t('actions.cancel')}
        variant="destructive"
        onConfirm={() => { if (onCancelEvent) withSave(onCancelEvent)(); }}
      />

      <ConfirmationDialog
        open={confirmCompleteEvent}
        onOpenChange={setConfirmCompleteEvent}
        title={t('form.confirmCompleteEvent.title')}
        description={t('form.confirmCompleteEvent.description')}
        confirmLabel={t('form.confirmCompleteEvent.confirm')}
        cancelLabel={t('actions.cancel')}
        onConfirm={() => { if (onCompleteEvent) withSave(onCompleteEvent)(); }}
      />

      <ConfirmationDialog
        open={confirmReopenEvent}
        onOpenChange={setConfirmReopenEvent}
        title={t('form.confirmReopen.title')}
        description={t('form.confirmReopen.description')}
        confirmLabel={t('form.confirmReopen.confirm')}
        cancelLabel={t('actions.cancel')}
        onConfirm={withSave(onReopen)}
      />

      {event && (
        <EventSeriesDialog
          open={seriesDialogOpen}
          onOpenChange={setSeriesDialogOpen}
          source={event}
          onlineDateMondayOnly={isPartner}
        />
      )}
    </>
  );
}

/**
 * Kopiert den Key der VA in die Zwischenablage (Testen/Support: der Key wird für commercetools,
 * Logs und die ticket-api ständig gebraucht). Rückmeldung am Icon selbst: kurz ein Haken,
 * bei einem Fehler (z. B. gesperrter Zwischenablage-Zugriff) ein rotes Kreuz.
 */
function CopyKeyButton({ value }: { value: string }) {
  const { t } = useTranslation('events');
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setState('copied');
    } catch {
      setState('failed');
    }
    setTimeout(() => setState('idle'), 1500);
  }

  const label = state === 'copied' ? t('form.keyCopied') : t('form.copyKey');
  return (
    <button
      type="button"
      onClick={() => void copy()}
      title={label}
      aria-label={label}
      className="inline-flex h-4 w-4 items-center justify-center rounded text-muted-foreground/60 hover:text-foreground"
    >
      {state === 'copied' ? (
        <Check className="h-3 w-3 text-green-600" />
      ) : state === 'failed' ? (
        <X className="h-3 w-3 text-red-600" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </button>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="text-base font-semibold text-foreground">{children}</h3>;
}

/** Horizontal separator between form blocks (the "---" marks from the spec).
 *  No own margin — the parent's space-y provides padding above and below. */
function Divider() {
  return <div className="border-t" />;
}

function Field({
  label,
  error,
  labelClassName,
  children,
  name,
  required,
  hint,
}: {
  label?: string;
  error?: string;
  labelClassName?: string;
  children: React.ReactNode;
  /** Schlüssel aus validate()'s Fehler-Objekt — Ankerpunkt zum Hinspringen. */
  name?: string;
  /**
   * Pflichtfeld-Kennzeichnung (Nutzervorgabe, 21.09.2026) — als Bedingung statt
   * fester Wert übergeben, wo ein Feld nur je nach anderen Einstellungen
   * pflichtig ist (z. B. Zahlungsfrist nur bei costEnabled): der Stern
   * schaltet dann automatisch mit, statt an einer Kopie der validate()-Logik
   * hängen zu bleiben, die aus dem Gleichschritt geraten könnte.
   */
  required?: boolean;
  /**
   * Kleiner Erklärtext mit Info-Icon, zwischen Label und Feld (29.09.2026,
   * Nutzervorgabe) — siehe promotingDepots/description für die beiden
   * ersten Verwendungen.
   */
  hint?: React.ReactNode;
}) {
  return (
    <div className="space-y-1" data-field={name}>
      {label && (
        <label className={`text-xs font-medium text-muted-foreground ${labelClassName ?? ''}`}>
          {label}
          {required && <span className="text-red-600"> *</span>}
        </label>
      )}
      {hint && (
        <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>{hint}</span>
        </div>
      )}
      {children}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

interface FooterProps {
  mode: DialogMode;
  status?: EventStatus;
  isCentral: boolean;
  /** Depot-scoped role on an already-submitted event: qty only. */
  frozenForPartner: boolean;
  /** Steuert Label/Verhalten des Speichern-Buttons (Nutzervorgabe, 18.09.2026):
   *  "Speichern & weiter" auf Tab 1/2, "Speichern" auf dem letzten Tab. */
  activeTab: 1 | 2 | 3;
  onCancel: () => void;
  /** Absent on tab 1 — goes to the previous tab (24.09.2026). */
  onBack?: () => void;
  onSave: () => void;
  onSaveAndContinue: () => void;
  onSubmit: () => void;
  onApprove: () => void;
  onReject: () => void;
  onReopen: () => void;
  /** Absent unless status is Completed. */
  onOpenRefund?: () => void;
  t: TFunction<'events'>;
}

/**
 * Vereinheitlichtes Layout (25.09.2026, Nutzervorgabe) — ersetzt die zuvor
 * sieben Branch-spezifischen Return-Blöcke: links immer "Abbrechen", dazu
 * "Zurückweisen" nur für Zentrale+InReview; rechts "Zurück" (nur beim
 * Anlegen/Bearbeiten) gefolgt von genau einem "mittleren" Speichern-Knopf und
 * genau einem "äußersten" Übergangs-Knopf, je nach Status/Rolle/Modus.
 * "Stornieren"/"Stattgefunden markieren"/"Löschen" sind in den Kopfbereich
 * gewandert (siehe EventFormDialog's Tab-Zeile) — sie waren schon vorher rein
 * durch die jeweils übergebenen Handler gegated (canCancelNow/canCompleteNow/
 * die serverseitig gespiegelte canDeleteRow-Regel in EventListPage.tsx), nicht
 * durch die Branch-Struktur hier, weshalb das Verschieben keine
 * Berechtigungslogik ändert.
 */
function FooterActions({
  mode, status, isCentral, frozenForPartner, activeTab, onCancel, onBack, onSave, onSaveAndContinue, onSubmit, onApprove, onReject, onReopen,
  onOpenRefund, t,
}: FooterProps) {
  // Rückfrage vor dem Schließen (Nutzervorgabe, 19.09.2026) — lokaler State
  // statt im Elternteil, weil `cancel` unten unverändert in jedem der frühen
  // Returns wiederverwendet wird; die Bestätigung reist mit dem Button statt
  // an jeder Stelle einzeln verdrahtet zu werden.
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Rückfrage vor "Zur Prüfung" (24.09.2026, Nutzervorgabe) — gleiches lokale
  // Muster wie confirmDiscard oben.
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const submitAction = (
    <>
      <Button className="gap-2" onClick={() => setConfirmSubmit(true)}>
        <Send className="h-4 w-4" />{t('actions.submit')}
      </Button>
      <ConfirmationDialog
        open={confirmSubmit}
        onOpenChange={setConfirmSubmit}
        title={t('form.confirmSubmit.title')}
        description={t('form.confirmSubmit.description')}
        confirmLabel={t('form.confirmSubmit.confirm')}
        cancelLabel={t('actions.cancel')}
        onConfirm={onSubmit}
      />
    </>
  );
  const cancel = (
    <>
      <Button variant="outline" onClick={() => setConfirmDiscard(true)}>{t('actions.cancel')}</Button>
      <ConfirmationDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title={t('form.confirmDiscard.title')}
        description={t('form.confirmDiscard.description')}
        confirmLabel={t('form.confirmDiscard.confirm')}
        cancelLabel={t('form.confirmDiscard.cancel')}
        onConfirm={onCancel}
      />
    </>
  );

  const isLastTab = activeTab === 3;
  const showReject = isCentral && status === EventStatus.InReview;
  const showBack = (mode === 'create' || mode === 'edit') && !!onBack;

  // Genau ein "mittlerer" Speichern-Knopf, je nach Status/Rolle/Modus —
  // dieselbe Priorität wie zuvor die Branch-Reihenfolge (Completed →
  // Cancelled → Zentrale+InReview → Zentrale+(Approved|Online) →
  // frozenForPartner → Anlegen/Bearbeiten → Fallback ohne Knopf).
  let middleAction: ReactNode = null;
  if (status === EventStatus.Completed || frozenForPartner) {
    middleAction = (
      <Button variant="outline" className="gap-2" onClick={onSave}>
        <Save className="h-4 w-4" />{t('actions.saveQty')}
      </Button>
    );
  } else if (isCentral && (status === EventStatus.Approved || status === EventStatus.Online)) {
    middleAction = (
      // Bug fix 18.09.2026: die Felder sind für Zentrale/Admin hier nicht
      // gesperrt (readOnly/qtyOnlyMode greifen nur für Partner bzw. ab
      // "Stattgefunden"), aber es gab bislang keinen Weg, Änderungen an
      // einer schon freigegebenen VA auch zu speichern.
      <Button variant="outline" className="gap-2" onClick={onSave}>
        <Save className="h-4 w-4" />{t('actions.save')}
      </Button>
    );
  } else if (mode === 'create' || mode === 'edit') {
    // "Speichern & weiter" (blau) auf Tab 1/2, "Speichern" (outline) auf dem
    // letzten Tab (18.09.2026, Nutzervorgabe).
    middleAction = (
      <Button
        variant={isLastTab ? 'outline' : 'default'}
        className="gap-2"
        onClick={isLastTab ? onSave : onSaveAndContinue}
      >
        <Save className="h-4 w-4" />{isLastTab ? t('actions.save') : t('actions.saveAndContinue')}
      </Button>
    );
  }

  // Genau ein "äußerster" Übergangs-Knopf, dieselbe Priorität wie oben.
  let rightmostAction: ReactNode = null;
  if (status === EventStatus.Completed) {
    rightmostAction = onOpenRefund ? (
      <Button className="gap-2" onClick={onOpenRefund}>
        {t('actions.openRefund')}
      </Button>
    ) : null;
  } else if (status === EventStatus.Cancelled) {
    // Abgesagt: nicht endgültig (Denkfehler behoben, 09.09.2026) — Zentrale
    // kann wie bei einer freigegebenen VA "wieder öffnen".
    rightmostAction = isCentral ? (
      <Button variant="outline" className="gap-2" onClick={onReopen}>
        <Undo2 className="h-4 w-4" />{t('actions.reopen')}
      </Button>
    ) : null;
  } else if (isCentral && status === EventStatus.InReview) {
    rightmostAction = (
      <Button className="gap-2 bg-green-600 hover:bg-green-700" onClick={onApprove}>
        <Check className="h-4 w-4" />{t('actions.approve')}
      </Button>
    );
  } else if (isCentral && (status === EventStatus.Approved || status === EventStatus.Online)) {
    rightmostAction = (
      <Button variant="outline" className="gap-2" onClick={onReopen}>
        <Undo2 className="h-4 w-4" />{t('actions.reopen')}
      </Button>
    );
  } else if ((mode === 'create' || mode === 'edit') && isLastTab && canSubmitForReview(status)) {
    // "Zur Prüfung" nur noch auf dem letzten Tab, nicht mehr auf jedem
    // (24.09.2026, Nutzervorgabe) — und nur, wo der Übergang überhaupt möglich ist
    // (03.11.2026): ein Partner, der eine VA "in Prüfung" öffnet, sah den Knopf sonst
    // trotzdem, weil die Tabelle jede Zeile im Modus 'edit' öffnet.
    rightmostAction = submitAction;
  }

  return (
    <div className="flex w-full justify-between">
      <div className="flex gap-2">
        {cancel}
        {showReject && (
          <Button variant="destructive" className="gap-2" onClick={onReject}>
            <X className="h-4 w-4" />{t('actions.reject')}
          </Button>
        )}
      </div>
      <div className="flex gap-2">
        {showBack && (
          <Button variant="outline" className="gap-2" onClick={onBack}>
            {t('actions.back')}
          </Button>
        )}
        {middleAction}
        {rightmostAction}
      </div>
    </div>
  );
}

